// frontend/src/pages/admin/AnalyseTicgn.jsx
// Miroir de AnalyseCspe.jsx — adapté pour la TICGN (taxes gaz naturel)
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useOutletContext, useParams, useLocation, Link, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabaseClient';
import {
    FiFileText, FiCheckSquare, FiXSquare, FiArrowLeft,
    FiDownload, FiAlertTriangle, FiClock, FiChevronDown, FiChevronUp,
    FiEye, FiX, FiLoader, FiCheck, FiDownloadCloud, FiLayers,
    FiSend, FiEdit3, FiUser, FiBarChart2, FiTarget,
    FiRefreshCw, FiUpload, FiCpu, FiCheckCircle, FiPenTool, FiTrash2, FiSearch, FiFilter, FiSettings
} from 'react-icons/fi';
import { FaEuroSign } from 'react-icons/fa';
import axios from 'axios';
import toast from 'react-hot-toast';
import { identifierFournisseursSansBloquer, avertissementNonIdentifiees } from '../../utils/identificationFournisseurs';
import { buildStoragePayload } from '../../utils/storagePayload';
import SignedContractsTICGN from '../../components/ticgn/SignedContractsTICGN';
import TicgnInvoiceEditorModal from '../../components/ticgn/TicgnInvoiceEditorModal';
import PdlSiretEditorModal from '../../components/cspe/PdlSiretEditorModal';
import AccountRaisonSocialeEditorModal from '../../components/cspe/AccountRaisonSocialeEditorModal';
import { confirmToast } from '../../lib/confirmToast';

// ===== SYSTÈME D'ONGLETS COMPACT =====
const TabSystem = ({ activeTab, onTabChange }) => {
  const tabs = [
    { id: 'analyse', label: 'Analyse TICGN', icon: FiBarChart2 },
  ];

  return (
    <div className="border-b border-gray-200 bg-white">
      <nav className="flex space-x-2 px-2" aria-label="Tabs">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => onTabChange(tab.id)}
            className={`${
              activeTab === tab.id
                ? 'border-teal-500 text-teal-600 bg-teal-50'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } group inline-flex items-center py-1.5 px-2 border-b-2 font-semibold text-xs transition-all duration-200 rounded-t`}
          >
            <tab.icon
              className={`${
                activeTab === tab.id ? 'text-teal-500' : 'text-gray-400 group-hover:text-gray-500'
              } -ml-0.5 mr-1 h-3 w-3`}
            />
            {tab.label}
          </button>
        ))}
      </nav>
    </div>
  );
};

// ===== HEADER COMPACT =====
const CompactHeader = ({ client, loading, onRefresh, selectedYear, onEditRaisonSociale }) => {
  return (
    <div className="bg-gradient-to-r from-blue-600 to-teal-700 text-white px-3 py-2">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center space-x-2 min-w-0">
          <Link
            to="/dashboard/admin/analyse-ticgn"
            className="p-1 hover:bg-white/10 rounded transition-colors"
            title="Retour"
          >
            <FiArrowLeft className="h-4 w-4" />
          </Link>

          <div className="min-w-0">
            <h1 className="text-sm font-semibold truncate flex items-center gap-1">
              <span>Analyse TICGN - {client?.company_name || client?.full_name || 'Client'}</span>
              {(client?.account_id || client?.id) && onEditRaisonSociale && (
                <button
                  onClick={onEditRaisonSociale}
                  className="p-0.5 rounded hover:bg-white/20 transition-colors"
                  title="Modifier la raison sociale (apparaît sur les CERFA et contrat)"
                  aria-label="Modifier la raison sociale"
                >
                  <FiEdit3 className="h-3 w-3" />
                </button>
              )}
            </h1>

            <div className="flex items-center flex-wrap gap-x-2 gap-y-1 text-[10px] opacity-90 mt-0.5">
              <span className="bg-white/10 px-1.5 py-0.5 rounded">
                Account ID: {client?.account_id}
              </span>
              <span className="opacity-75">•</span>
              <span className="opacity-90">Taxes gaz naturel</span>
              {selectedYear && (
                <>
                  <span className="opacity-75">•</span>
                  <span className="opacity-90">{selectedYear}</span>
                </>
              )}
            </div>
          </div>
        </div>

        <button
          onClick={onRefresh}
          disabled={loading}
          className="p-1.5 hover:bg-white/10 rounded transition-colors disabled:opacity-50"
          title="Actualiser"
        >
          <FiRefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>
    </div>
  );
};

// ===== CONSTANTES =====
const DOCUMENT_CATEGORIES = {
  'documents_financiers': "Documents financiers (Liasse fiscale)",
};

const DOCUMENT_CONFIG = [
  {
    key: 'documents_financiers',
    name: "Documents financiers (Liasse fiscale)",
    optional: false
  },
];

const STATUS_DISPLAY = {
  'uploaded': { text: 'Reçu', color: 'bg-blue-100 text-blue-800', icon: <FiFileText /> },
  'En attente': { text: 'En attente', color: 'bg-yellow-100 text-yellow-800', icon: <FiClock /> },
  'En attente de validation': { text: 'En attente', color: 'bg-yellow-100 text-yellow-800', icon: <FiClock /> },
  'En attente de validation manuelle': { text: 'Validation manuelle requise', color: 'bg-orange-100 text-orange-800', icon: <FiEdit3 /> },
  'Validé': { text: 'Validé', color: 'bg-green-100 text-green-800', icon: <FiCheckSquare /> },
  'Validé manuellement': { text: 'Validé manuellement', color: 'bg-emerald-100 text-emerald-800', icon: <FiCheckSquare /> },
  'Rejeté': { text: 'Rejeté', color: 'bg-red-100 text-red-800', icon: <FiAlertTriangle /> },
  'Rejeté par admin': { text: 'Rejeté par admin', color: 'bg-red-100 text-red-800', icon: <FiAlertTriangle /> },
  'À fournir': { text: 'À fournir', color: 'bg-slate-200 text-slate-700', icon: <FiFileText /> },
};

const SUPABASE_DEFAULT_BUCKET = 'user-documents';
const BUCKET_NAME = 'generated-cerfas';
const SIGNED_CERFA_BUNDLES_BUCKET = 'cerfa-bundles';

// ===== FONCTIONS UTILITAIRES =====
const buildStoragePath = (doc) => {
  if (doc?.s3_key && doc?.s3_bucket) return `s3://${doc.s3_bucket}/${doc.s3_key}`;
  if (doc?.old_supabase_storage_path) {
    const s = doc.old_supabase_storage_path;
    if (s.startsWith('supabase://') || s.startsWith('s3://')) return s;
    return `supabase://user-documents/${s}`;
  }
  throw new Error("Emplacement du fichier introuvable (S3 ou Supabase).");
};

const getCategoryStatus = (documentsInCategory) => {
  if (!documentsInCategory || documentsInCategory.length === 0) {
    return { text: 'À fournir', color: 'bg-slate-200 text-slate-700' };
  }
  const allValidated = documentsInCategory.every(doc => doc.status === 'Validé');
  if (allValidated) return { text: 'Complet', color: 'bg-green-100 text-green-800' };
  const someRejected = documentsInCategory.some(doc => doc.status === 'Rejeté');
  if (someRejected) return { text: 'Action Requise', color: 'bg-red-100 text-red-800' };
  const somePending = documentsInCategory.some(doc =>
    doc.status === 'En attente' || doc.status === 'En attente de validation'
  );
  if (somePending) return { text: 'En attente', color: 'bg-yellow-100 text-yellow-800' };
  const someProvidedButNotFinal = documentsInCategory.some(doc =>
    doc.status && doc.status !== 'À fournir'
  );
  if (someProvidedButNotFinal) return { text: 'Partiel', color: 'bg-yellow-100 text-yellow-700' };
  return { text: 'À fournir', color: 'bg-slate-200 text-slate-700' };
};

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || 'document.pdf';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ===== COMPOSANT SECTION DOCUMENT COMPACT =====
const DocumentCategorySection = ({
  categoryKey,
  categoryName,
  isOptional,
  documents,
  onValidate,
  onReject,
  onProcessLiasse,
  onValidateAllInCategory,
  children,
}) => {
  const [isOpen, setIsOpen] = useState(true);

  const docsNeedOcr = documents.filter(doc =>
    doc.document_type_id === 'documents_financiers' && !doc.ocr_done
  );
  const hasDocsToOcr = docsNeedOcr.length > 0;

  const documentsEnAttenteDeValidation = documents.filter(doc =>
    doc.status === 'En attente' || doc.status === 'En attente de validation'
  );
  const categoryStatus = getCategoryStatus(documents);

  return (
    <section key={categoryKey} className="bg-white rounded-md shadow-sm border border-gray-200">
      <div
        className="flex justify-between items-center p-3 hover:bg-slate-50/70 rounded-t-md cursor-pointer"
        onClick={() => setIsOpen(!isOpen)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setIsOpen(!isOpen); }}
        aria-expanded={isOpen}
      >
        <div className="flex items-center">
          <h3 className="text-sm font-medium text-slate-800">{categoryName}</h3>
          {isOptional && (
            <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-sky-100 text-sky-800">
              Optionnel
            </span>
          )}
          <span className={`ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${categoryStatus.color}`}>
            {categoryStatus.text}
          </span>
        </div>
        <div className="flex items-center space-x-2">
          {documents.length > 0 && hasDocsToOcr && (
              <button
                onClick={(e) => { e.stopPropagation(); docsNeedOcr.forEach(d => onProcessLiasse(d)); }}
                className="px-2 py-1 text-xs font-medium text-white rounded bg-blue-600 hover:bg-blue-700 transition-colors"
              >
                <FiCpu className="inline h-3 w-3 mr-1" /> Lancer OCR
              </button>
          )}
          {isOpen ? <FiChevronUp className="h-4 w-4 text-slate-500" /> : <FiChevronDown className="h-4 w-4 text-slate-500" />}
        </div>
      </div>
      {isOpen && (
        <div className="border-t border-slate-200">
          {children}
        </div>
      )}
    </section>
  );
};

const FileUploadZone = ({ onFileUpload }) => {
  const [isDragging, setIsDragging] = useState(false);

  const handleDragEnter = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); };
  const handleDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); };
  const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); };
  const handleDrop = (e) => {
    e.preventDefault(); e.stopPropagation(); setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      onFileUpload(e.dataTransfer.files[0]);
      e.dataTransfer.clearData();
    }
  };
  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) onFileUpload(e.target.files[0]);
  };

  return (
    <div
      className={`p-4 border-2 border-dashed rounded-md text-center cursor-pointer transition-colors ${
        isDragging ? 'border-teal-500 bg-teal-50' : 'border-gray-300 hover:border-teal-400'
      }`}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      onClick={() => document.getElementById('liasse-upload-input-ticgn').click()}
    >
      <FiUpload className="mx-auto h-6 w-6 text-gray-400" />
      <p className="mt-2 text-sm text-gray-600">
        Déposez la liasse fiscale ici ou <span className="font-semibold text-teal-600">cliquez pour parcourir</span>
      </p>
      <p className="text-xs text-gray-500 mt-1">PDF uniquement</p>
      <input
        id="liasse-upload-input-ticgn"
        type="file"
        className="hidden"
        accept="application/pdf"
        onChange={handleFileChange}
      />
    </div>
  );
};

// ===== COMPOSANT SECTION WORKFLOW =====
const WorkflowStepSection = ({
  stepIndex, title, subtitle, icon: Icon, isActive, isOpen, onToggle, children
}) => {
  return (
    <div className={`bg-white rounded-md shadow-sm border transition-all duration-200 ${
      isActive ? 'border-teal-500 border-2' : 'border-gray-200'
    }`}>
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-2 text-left hover:bg-gray-50 rounded-t-md"
        aria-expanded={isOpen}
      >
        <div className="flex items-center space-x-2 min-w-0">
          <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${
            isActive ? 'bg-teal-500 text-white' : 'bg-gray-200 text-gray-700'
          }`}>
            {stepIndex + 1}
          </div>
          <div className="min-w-0">
            <h2 className="text-xs font-semibold text-gray-900 truncate">{title}</h2>
            {subtitle && <p className="text-[10px] text-gray-600 mt-0.5 line-clamp-1">{subtitle}</p>}
          </div>
        </div>
        <div className="flex items-center space-x-2">
          {Icon && <Icon className={`h-3.5 w-3.5 ${isActive ? 'text-teal-600' : 'text-gray-500'}`} />}
          {isOpen ? <FiChevronUp className="h-4 w-4 text-gray-500" /> : <FiChevronDown className="h-4 w-4 text-gray-500" />}
        </div>
      </button>
      {isOpen && (
        <div className="border-t p-2 bg-gray-50/50">
          {children}
        </div>
      )}
    </div>
  );
};

// ===== SECTION TITLE =====
const SectionTitle = ({ title, subtitle, icon: Icon, badge, children }) => {
  return (
    <div className="bg-white rounded-md shadow-sm border border-gray-200 mb-2">
      <div className="p-2 border-b border-gray-200">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center space-x-2 min-w-0">
            {Icon && <Icon className="h-3.5 w-3.5 text-teal-600" />}
            <div className="min-w-0">
              <h2 className="text-xs font-semibold text-gray-900 truncate">{title}</h2>
              {subtitle && <p className="text-[10px] text-gray-600 mt-0.5 truncate">{subtitle}</p>}
            </div>
          </div>
          {badge !== undefined && badge !== null && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-teal-100 text-teal-800 shrink-0">
              {badge}
            </span>
          )}
        </div>
      </div>
      <div className="p-2">
        {children}
      </div>
    </div>
  );
};

// ===== COMPOSANT PRINCIPAL =====
const AnalyseTicgn = () => {
  const { session } = useOutletContext();
  const { accountId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);

  const now = new Date();
  const passedYear = location.state?.year ? Number(location.state.year) : null;

  const [selectedYear, setSelectedYear] = useState(() =>
    accountId ? (passedYear ?? 2023) : now.getFullYear()
  );

  const [selectedMonth, setSelectedMonth] = useState(
    typeof location.state?.month === 'number' ? location.state.month : 0
  );

  // États pour la vue détaillée
  const [client, setClient] = useState(null);
  const [pceList, setPceList] = useState([]);
  const [allUserDocuments, setAllUserDocuments] = useState([]);
  const [fiscalData, setFiscalData] = useState(null);
  const [generatedCerfas, setGeneratedCerfas] = useState([]);

  const [isGeneratingERAG, setIsGeneratingERAG] = useState(false);
  const [isGeneratingTICVAGC, setIsGeneratingTICVAGC] = useState(false);
  const [gazIntensite, setGazIntensite] = useState(null);
  const [isGenerating3310TIC, setIsGenerating3310TIC] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isProcessingLiasse, setIsProcessingLiasse] = useState(false);
  const [isMarkingVerificationComplete, setIsMarkingVerificationComplete] = useState(false);
  const [isConfirmingCerfaGen, setIsConfirmingCerfaGen] = useState(false);
  const [isCombiningCerfas, setIsCombiningCerfas] = useState(false);
  const [isNotifying, setIsNotifying] = useState(false);

  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [signedCerfaDocuments, setSignedCerfaDocuments] = useState([]);
  const [success, setSuccess] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [activeTab, setActiveTab] = useState('analyse');
  const [generatingPceId, setGeneratingPceId] = useState(null);
  const [openStepIndex, setOpenStepIndex] = useState(0);
  const prevWorkflowStepRef = useRef(null);
  const [dossierYear, setDossierYear] = useState(null);
  const [premierBilanConfig, setPremierBilanConfig] = useState({});
  const [manualLinesCountByPce, setManualLinesCountByPce] = useState({});
  const [manualModeFlags, setManualModeFlags] = useState({});
  const [isTicgnModalOpen, setIsTicgnModalOpen] = useState(false);
  const [ticgnModalPce, setTicgnModalPce] = useState(null);
  const [isSiretModalOpen, setIsSiretModalOpen] = useState(false);
  const [siretModalPce, setSiretModalPce] = useState(null);
  const [isRaisonSocialeModalOpen, setIsRaisonSocialeModalOpen] = useState(false);
  const [exonerationOption, setExonerationOption] = useState('J'); // 'J' = G11, 'K' = G12
  const [pourcentageExonere, setPourcentageExonere] = useState(100);

  const openTicgnEditor = (pce) => {
    setTicgnModalPce(pce);
    setIsTicgnModalOpen(true);
  };

  const openSiretEditor = (pce) => {
    setSiretModalPce(pce);
    setIsSiretModalOpen(true);
  };

  const handlePceSaved = (updatedPce) => {
    setPceList((prev) => prev.map((p) => (p.id === updatedPce.id ? { ...p, ...updatedPce } : p)));
  };

  const MOIS = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'];

  useEffect(() => {
    const y = new Date().getFullYear();
    if (accountId) {
      setSelectedYear(passedYear ?? 2023);
    } else {
      setSelectedYear(y);
    }
  }, [accountId]);

  function periodLabel(year, month) {
    if (!year) return '';
    if (!month) return `Année ${year}`;
    return `${MOIS[month - 1]} ${year}`;
  }

  // ===== FETCH GENERATED CERFAS =====
  const fetchGeneratedCerfas = useCallback(async () => {
    if (!accountId) {
      setGeneratedCerfas([]);
      return;
    }
    try {
      const { data, error } = await supabase
        .from('generated_cerfa_documents')
        .select('*')
        .eq('account_id', accountId)
        .eq('fiscal_year', selectedYear)
        .eq('tax_type', 'TICGN')
        .order('generated_at', { ascending: false });
      if (error) throw error;
      setGeneratedCerfas(data || []);
    } catch (error) {
      console.error("[fetchGeneratedCerfas TICGN] Erreur:", error.message);
    }
  }, [accountId, selectedYear]);

  // Charger la gaz-intensité depuis valeur_ajoutee_calculee
  const fetchGazIntensite = useCallback(async () => {
    if (!accountId) return;
    try {
      const { data } = await supabase
        .from('valeur_ajoutee_calculee')
        .select('intensite_calculee')
        .eq('account_id', accountId)
        .eq('annee_fiscale', selectedYear)
        .eq('source_cerfa_type', 'CERFA_TIC_VA_GC')
        .maybeSingle();
      if (data) {
        setGazIntensite(data.intensite_calculee);
      }
    } catch (e) {
      // silencieux
    }
  }, [accountId, selectedYear]);

  // ===== FETCH CLIENT DATA =====
  const fetchClientData = useCallback(async () => {
    if (!accountId) return;
    setLoading(true);

    try {
      // 1. Infos client
      const { data: clientRow, error: clientViewError } = await supabase
        .from("clients_view")
        .select("*")
        .eq("account_id", accountId)
        .single();

      if (clientViewError) throw clientViewError;
      if (!clientRow) {
        setClient(null);
        setLoading(false);
        return;
      }

      // 2. Données Pappers
      let pappersRow = null;
      if (clientRow.siren) {
        const { data: pappersOne, error: pappersErr } = await supabase
          .from("pappers_data")
          .select("*")
          .eq("siren", clientRow.siren)
          .order("updated_at", { ascending: false })
          .limit(1)
          .maybeSingle();
        if (pappersErr) console.warn("[AnalyseTicgn] pappers_data error:", pappersErr.message);
        else pappersRow = pappersOne || null;
      }

      // 3. Données account
      const { data: accountSpecifics, error: accountSpecificsError } = await supabase
        .from("accounts")
        .select(`
          meter_reference,
          dossier_stage,
          dossier_progress_percentage,
          onboarding_completed,
          contract_signed_at
        `)
        .eq("id", accountId)
        .maybeSingle();

      if (accountSpecificsError) console.warn("[AnalyseTicgn] accounts error:", accountSpecificsError.message);

      let enrichedClientData = {
        ...clientRow,
        pappers_data: pappersRow,
        ...(accountSpecifics || {})
      };
      setClient(enrichedClientData);

      // 4. Dossier year TICGN
      const dossierYearRes = await supabase
        .from('ticgn_dossier_years')
        .select('*')
        .eq('account_id', accountId)
        .eq('fiscal_year', selectedYear)
        .maybeSingle();
      setDossierYear(dossierYearRes.data || null);

      // 5. Chargement PCE + documents + fiscal
      const [
        docsRes,
        fiscalResRaw,
        pcesRes,
        generatedCerfasRes,
        signedCerfasRes,
      ] = await Promise.all([
        supabase.from("user_uploaded_documents").select("*").eq("account_id", accountId),
        supabase.from("fiscal_declarations_data").select("*").eq("account_id", accountId).order("exercice_end_date", { ascending: false }).limit(1),
        supabase.from("pce").select("id, pce_number, site_name, siret_etablissement, naf_code").eq("account_id", accountId),
        supabase.from("generated_cerfa_documents")
          .select("*")
          .eq("account_id", accountId)
          .eq("fiscal_year", selectedYear)
          .eq("tax_type", "TICGN")
          .order("generated_at", { ascending: false }),
        supabase.from("signed_cerfas").select("*")
          .eq("account_id", accountId)
          .eq("fiscal_year", selectedYear)
          .eq("tax_type", "TICGN")
          .order("signed_at", { ascending: false }),
      ]);

      if (docsRes.error) throw docsRes.error;
      if (pcesRes.error) throw pcesRes.error;

      if (pcesRes.data) {
        setPceList(pcesRes.data);
      }

      const fiscalRow = (fiscalResRaw.data && fiscalResRaw.data[0]) || null;
      setFiscalData(fiscalRow || { exercice_end_date: null });

      const normalizedDocs = (docsRes.data || []).map(d => ({
        ...d,
        ocr_done: !!d.ocr_done,
        year: d.year != null ? Number(d.year) : null,
      }));
      setAllUserDocuments(normalizedDocs);
      setGeneratedCerfas(generatedCerfasRes.data || []);
      setSignedCerfaDocuments(signedCerfasRes.data || []);

    } catch (error) {
      console.error("[AnalyseTicgn] Erreur globale:", error.message);
      setClient(null);
      setAllUserDocuments([]);
      setPceList([]);
      setFiscalData(null);
    } finally {
      setLoading(false);
    }
  }, [accountId, selectedYear]);

  // ===== EFFECTS =====
  useEffect(() => {
    if (accountId) {
      fetchClientData();
    } else {
      setLoading(false);
    }
  }, [accountId, fetchClientData]);

  useEffect(() => {
    if (!accountId) return;
    const channel = supabase
      .channel(`public:accounts:id=eq.${accountId}`)
      .on('postgres_changes', {
        event: 'UPDATE', schema: 'public', table: 'accounts', filter: `id=eq.${accountId}`
      }, (payload) => {
        setClient(prevClient => ({ ...prevClient, ...payload.new }));
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [accountId]);

  useEffect(() => {
    if (!accountId || !selectedYear) return;
    const dyChannel = supabase
      .channel(`ticgn_dossier_years:${accountId}:${selectedYear}`)
      .on('postgres_changes', {
        event: '*', schema: 'public', table: 'ticgn_dossier_years', filter: `account_id=eq.${accountId}`,
      }, (payload) => {
        if (payload.new && payload.new.fiscal_year === selectedYear) {
          setDossierYear(payload.new);
        }
      })
      .subscribe();
    return () => { supabase.removeChannel(dyChannel); };
  }, [accountId, selectedYear]);

  useEffect(() => {
    if (!accountId) return;
    const docChannel = supabase
      .channel(`public:user_uploaded_documents:account_id=eq.${accountId}:ticgn`)
      .on('postgres_changes', {
        event: '*', schema: 'public', table: 'user_uploaded_documents', filter: `account_id=eq.${accountId}`
      }, () => {
        fetchClientData();
      })
      .subscribe();
    return () => { supabase.removeChannel(docChannel); };
  }, [accountId, fetchClientData]);

  useEffect(() => {
    if (client && client.account_id) {
      fetchGeneratedCerfas();
      fetchGazIntensite();
    }
  }, [client, fetchGeneratedCerfas, fetchGazIntensite]);

  // ===== HANDLERS =====

  const handleValidateDocument = async (docId) => {
    const doc = allUserDocuments.find(d => d.id === docId);
    if (doc?.document_type_id === 'documents_financiers' && !doc.ocr_done) {
      toast.error("Action impossible : Vous devez d'abord lancer l'analyse OCR du document.");
      return;
    }
    const toastId = toast.loading("Validation du document...");
    try {
      const { error } = await supabase
        .from('user_uploaded_documents')
        .update({ status: 'Validé', rejection_reason: null })
        .eq('id', docId);
      if (error) throw error;
      toast.success("Document validé avec succès !", { id: toastId });
      fetchClientData();
    } catch (error) {
      toast.error(`Erreur de validation : ${error.message}`, { id: toastId });
    }
  };

  const handleRejectDocument = async (docId, reason) => {
    if (!reason || reason.trim() === "") {
      toast.error("Un motif de rejet est requis.");
      return;
    }
    const toastId = toast.loading("Enregistrement du rejet...");
    try {
      const { data, error } = await supabase
        .from('user_uploaded_documents')
        .update({ status: 'Rejeté', rejection_reason: reason })
        .eq('id', docId)
        .select().single();
      if (error) throw error;
      if (data) {
        setAllUserDocuments(prevDocs =>
          prevDocs.map(doc => (doc.id === docId ? { ...doc, status: 'Rejeté', rejection_reason: reason } : doc))
        );
      }
      toast.success("Document rejeté.", { id: toastId });
    } catch (err) {
      console.error('Erreur inattendue rejet:', err);
      toast.error('Une erreur est survenue lors du rejet.', { id: toastId });
    }
  };

  const handleLiasseUpload = async (file) => {
    if (!file) return;
    const toastId = toast.loading('Préparation de l\'envoi de la liasse...');
    try {
      const { data: presignData } = await axios.post('/api/ticgn/documents/request-upload-url', {
        fileName: file.name,
        accountId: accountId
      }, {
        headers: { 'Authorization': `Bearer ${session.access_token}` }
      });
      if (!presignData.success) throw new Error("Impossible d'obtenir une URL d'upload.");

      toast.loading('Envoi du fichier en cours...', { id: toastId });
      await axios.put(presignData.data.uploadUrl, file, {
        headers: { 'Content-Type': file.type }
      });

      toast.loading('Finalisation...', { id: toastId });
      const payload = {
        s3Key: presignData.data.s3Key,
        originalFileName: file.name,
        fileType: file.type,
        fileSize: file.size,
        type: 'documents_financiers',
        year: selectedYear,
        accountId: accountId
      };
      await axios.post('/api/ticgn/documents/confirm-upload', payload, {
        headers: { 'Authorization': `Bearer ${session.access_token}` }
      });
      toast.success('Liasse fiscale envoyée avec succès !', { id: toastId });
      fetchClientData();
    } catch (error) {
      toast.error(error.response?.data?.message || "L'upload de la liasse a échoué.", { id: toastId });
    }
  };

  const handleDeleteLiasse = async (doc) => {
    const confirmed = await confirmToast({
      title: 'Supprimer cette liasse fiscale ?',
      subtitle: `"${doc.original_file_name}" sera définitivement supprimée.`,
      confirmLabel: 'Supprimer',
      variant: 'danger',
    });
    if (!confirmed) return;

    const toastId = toast.loading('Suppression en cours...');
    try {
      const { error: storageError } = await supabase.storage
        .from(doc.s3_bucket)
        .remove([doc.s3_key]);
      if (storageError) throw new Error(storageError.message);

      const { error: dbError } = await supabase
        .from('user_uploaded_documents')
        .delete()
        .eq('id', doc.id)
        .eq('account_id', accountId);
      if (dbError) throw new Error(dbError.message);

      toast.success('Liasse fiscale supprimée.', { id: toastId });
      fetchClientData();
    } catch (error) {
      console.error('Erreur suppression liasse:', error);
      toast.error(`Suppression échouée : ${error.message}`, { id: toastId });
    }
  };

  const handleProcessLiasse = useCallback(async (doc) => {
    if (!doc || !client || !client.account_id) {
      toast.error("Infos client ou document manquantes.");
      return;
    }
    if (isProcessingLiasse) return;

    setIsProcessingLiasse(true);
    const toastId = toast.loading("Analyse OCR de la liasse fiscale en cours...");

    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session) throw new Error("Session invalide ou expirée.");

      const token = session.access_token;
      const storagePath = buildStoragePath(doc);

      const response = await fetch(`/api/cspe/ocr/process-liasse-fiscale`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          storagePath,
          documentId: doc.id,
          accountId: client.account_id
        })
      });

      let result;
      try { result = await response.json(); }
      catch { throw new Error(`Erreur backend (${response.status})`); }

      if (!response.ok || !result?.success) {
        throw new Error(result?.message || "Échec traitement liasse.");
      }

      if (result.data) {
        const { error: updError } = await supabase
          .from('user_uploaded_documents')
          .update({
            ocr_done: true,
            status: 'En attente de validation',
          })
          .eq('id', doc.id);
        if (updError) console.error("Erreur update document post-OCR :", updError);
      }

      await fetchClientData();
      toast.success("Liasse fiscale traitée — en attente de validation.", { id: toastId });

    } catch (error) {
      console.error("Erreur liasse fiscale:", error);
      toast.error(`Erreur: ${error.message}`, { id: toastId });
    } finally {
      setIsProcessingLiasse(false);
    }
  }, [client, isProcessingLiasse, fetchClientData]);

  const handleValidateAllInCategory = async (documentsToValidate) => {
    if (documentsToValidate.length === 0) {
      toast.error("Aucun document 'En attente' à valider.");
      return;
    }
    const unparsedLiasses = documentsToValidate.filter(doc =>
      doc.document_type_id === 'documents_financiers' && !doc.ocr_done
    );
    if (unparsedLiasses.length > 0) {
      toast.error("Validation impossible : Une ou plusieurs liasses fiscales n'ont pas été analysées (OCR).");
      return;
    }
    const confirmed = await confirmToast({
      title: `Valider ${documentsToValidate.length} document${documentsToValidate.length > 1 ? 's' : ''} ?`,
      subtitle: 'Cette action marquera les documents sélectionnés comme "Validé".',
      confirmLabel: 'Valider',
      cancelLabel: 'Annuler',
      variant: 'warning',
    });
    if (!confirmed) return;

    const toastId = toast.loading(`Validation de ${documentsToValidate.length} document(s) en cours...`);
    try {
      const validationPromises = documentsToValidate.map(async (doc) => {
        const { error } = await supabase
          .from('user_uploaded_documents')
          .update({ status: 'Validé', rejection_reason: null })
          .eq('id', doc.id);
        if (error) throw new Error(`Échec pour "${doc.original_file_name}": ${error.message}`);
        return true;
      });
      const results = await Promise.allSettled(validationPromises);
      const successCount = results.filter(r => r.status === 'fulfilled').length;
      const errorCount = results.filter(r => r.status === 'rejected').length;
      if (errorCount === 0) {
        toast.success(`${successCount} document(s) validé(s) avec succès !`, { id: toastId });
      } else {
        toast.error(`${successCount} validé(s), ${errorCount} échec(s).`, { id: toastId, duration: 5000 });
      }
      fetchClientData();
    } catch (globalError) {
      console.error("Erreur critique validation lot:", globalError);
      toast.error("Une erreur inattendue est survenue.", { id: toastId });
    }
  };

  // ===== GÉNÉRATION CERFA ERA-G (par PCE) =====
  const handleGenerateCerfaERAG = useCallback(async (pce) => {
    const year = selectedYear;
    const pceId = pce.id;

    if (!client || !year) {
      toast.error("Données client ou année d'analyse manquantes.");
      return;
    }

    // Prérequis : liasse fiscale doit être traitée
    if (!fiscalData?.id) {
      toast.error("La liasse fiscale doit être traitée avant de générer l'ERA-G.");
      return;
    }

    const docsToProcess = (allUserDocuments || []).filter(
      doc =>
        doc.pce_id === pceId &&
        Number(doc.year) === year &&
        doc.document_type_id === 'factures_gaz'
    );

    const hasManual = (manualLinesCountByPce[pceId] || 0) > 0;
    const isManualOnly = docsToProcess.length === 0 && hasManual;

    if (docsToProcess.length === 0 && !hasManual) {
      toast.error(`Aucune facture gaz ni ligne manuelle pour ${pce.site_name} en ${year}.`);
      return;
    }

    setGeneratingPceId(pceId);
    const toastId = isManualOnly
      ? toast.loading(`Génération CERFA ERA-G (mode manuel) pour ${pce.site_name}...`)
      : toast.loading(`Lancement du pipeline d'analyse TICGN pour ${pce.site_name}...`);

    try {
      const token = session?.access_token;
      if (!token) throw new Error("Session expirée.");

      if (!isManualOnly) {
        toast.loading(`Étape 1/3 : Validation des factures gaz...`, { id: toastId });
        await Promise.all(docsToProcess.map(doc =>
          axios.post(`/api/ticgn/documents/uploads-s3/${doc.id}/validate`, {}, {
            headers: { 'Authorization': `Bearer ${token}` }
          })
        ));

        toast.loading(`Étape 2/3 : Identification des fournisseurs gaz...`, { id: toastId });
        // Même outil que l'ERA CSPE (utils/identificationFournisseurs.js) : un fournisseur
        // non reconnu (422) ne bloque pas, toute autre erreur bloque. Aucun parser gaz
        // n'existe : la route répond toujours 422 et l'ERA-G se calcule sur les lignes
        // enregistrées (saisie manuelle, lignes extraites ou corrigées). C'est le backend
        // qui refuse, avec un message clair, s'il n'y en a aucune — l'écran ne connaît que
        // les lignes manuelles et refuserait à tort un PCE couvert par d'autres lignes.
        // Pas de filtre « déjà identifiée » : aucun champ fournisseur n'existe pour le gaz.
        const nonIdentifiees = await identifierFournisseursSansBloquer(docsToProcess, doc =>
          axios.post('/api/ticgn/ocr/identify-provider', {
            documentId: doc.id,
            storagePath: doc.s3_key
              ? `s3://${doc.s3_bucket}/${doc.s3_key}`
              : doc.old_supabase_storage_path,
          }, {
            headers: { 'Authorization': `Bearer ${token}` }
          })
        );
        if (nonIdentifiees.length > 0) {
          nonIdentifiees.forEach(({ doc, message }) =>
            console.warn(`Identification fournisseur gaz échouée (${doc.original_file_name}) : ${message}`));
          toast(avertissementNonIdentifiees(nonIdentifiees, { taxe: 'TICGN', cerfa: "l'ERA-G" }), { icon: '⚠️', duration: 9000 });
        }
      }

      toast.loading(isManualOnly ? `Génération du CERFA ERA-G (lignes manuelles)...` : `Étape 3/3 : Génération du CERFA ERA-G...`, { id: toastId });

      const pbConfig = premierBilanConfig[`${pceId}-${year}`];

      const response = await fetch('/api/ticgn/documents/generate-era-g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          account_id: client.account_id,
          pce_id: pceId,
          annee_fiscale: year,
          fiscal_year: year,
          exoneration_option: exonerationOption || 'J',
          pourcentage_exonere: pourcentageExonere ?? 100,
          ...(pbConfig?.enabled && pbConfig?.startDate
            ? { exercice_start_date: pbConfig.startDate }
            : {}
          ),
        })
      });

      const result = await response.json();
      if (!response.ok) throw new Error(result.message || "Erreur lors de la génération PDF.");

      manualModeLockRef.current = false;
      toast.success(`CERFA ERA-G généré pour ${pce.site_name} !`, { id: toastId });

      await Promise.all([
        fetchGeneratedCerfas(),
        fetchClientData()
      ]);

    } catch (error) {
      console.error("Erreur Pipeline Analyse TICGN:", error);
      // Message du backend s'il y en a un (axios ne donne sinon que « Request failed with status code … »).
      toast.error(`Erreur : ${error.response?.data?.message || error.message}`, { id: toastId, duration: 6000 });
    } finally {
      setGeneratingPceId(null);
    }
  }, [client, selectedYear, allUserDocuments, session, fetchGeneratedCerfas, fetchClientData, premierBilanConfig, manualLinesCountByPce, exonerationOption, pourcentageExonere, fiscalData]);

  // ===== GÉNÉRATION CERFA TIC-VA-GC =====
  const handleGenerateCerfaTICVAGC = async () => {
    if (!client || !client.account_id || !fiscalData || !fiscalData.id) {
      toast.error("Les données du client ou l'ID des données fiscales sont manquantes.");
      return;
    }

    // Vérification de la présence de la liasse fiscale validée
    if (!isLiasseValidated || !fiscalData?.id) {
      toast.error("La liasse fiscale doit être importée et validée avant de générer le CERFA TIC-VA-GC.");
      return;
    }

    setIsGeneratingTICVAGC(true);
    const toastId = toast.loading("Génération du CERFA TIC-VA-GC en cours...");

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("Votre session est invalide.");

      // Toggle « Premier bilan » : la config est par PCE, mais l'exercice
      // fiscal est commun à tout le compte — n'importe quelle entrée activée
      // pour l'année sélectionnée porte donc la même date de début.
      // Sans ça, la date corrigée à la main n'atteignait que le CERFA ERA-G :
      // le TIC-VA-GC repartait sur la BDD et imprimait 12 mois.
      const pbStartDate = Object.entries(premierBilanConfig)
        .find(([key, cfg]) => key.endsWith(`-${selectedYear}`) && cfg?.enabled && cfg?.startDate)
        ?.[1]?.startDate;

      const payload = {
        fiscal_declaration_id: fiscalData.id,
        accountId: client.account_id,
        annee_fiscale: selectedYear,
        fiscal_year: selectedYear,
        ...(pbStartDate ? { exercice_start_date: pbStartDate } : {}),
      };

      const response = await fetch('/api/ticgn/documents/generate-tic-va-gc', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` },
        body: JSON.stringify(payload)
      });

      const result = await response.json();
      if (!response.ok) throw new Error(result.message || 'Erreur serveur.');

      toast.success('CERFA TIC-VA-GC généré avec succès !', { id: toastId });
      if (result.intensiteCalculee !== undefined) {
        setGazIntensite(result.intensiteCalculee);
      }
      fetchGeneratedCerfas();

    } catch (error) {
      toast.error(`Erreur : ${error.message}`, { id: toastId });
    } finally {
      setIsGeneratingTICVAGC(false);
    }
  };

  // ===== GÉNÉRATION CERFA 3310-TIC =====
  const handleGenerateCerfa3310TIC = async () => {
    if (!client || !client.account_id || !fiscalData || !fiscalData.id) {
      toast.error("Les données du client ou fiscales sont manquantes.");
      return;
    }

    setIsGenerating3310TIC(true);
    const toastId = toast.loading("Génération du CERFA 3310-TIC...");

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("Session invalide.");

      const payload = {
        accountId: client.account_id,
        annee_fiscale: selectedYear,
        fiscal_declaration_id: fiscalData.id,
        fiscal_year: selectedYear,
        tax_type: 'TICGN',
      };

      const response = await fetch('/api/ticgn/documents/generate-3310-tic', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` },
        body: JSON.stringify(payload)
      });

      const result = await response.json();
      if (!response.ok) throw new Error(result.message || "Erreur serveur.");

      toast.success('CERFA 3310-TIC généré avec succès !', { id: toastId });
      await fetchGeneratedCerfas();

    } catch (error) {
      toast.error(`Erreur : ${error.message}`, { id: toastId });
    } finally {
      setIsGenerating3310TIC(false);
    }
  };

  // ===== DOWNLOAD / PREVIEW / DELETE CERFAS =====
  const handleDownloadGeneratedCerfa = async (storagePath, fileName, documentType) => {
    try {
      const bucket = documentType === 'CERFA_BUNDLE' ? 'cerfa-bundles' : 'generated-cerfas';
      const { data, error } = await supabase.storage.from(bucket).download(storagePath);
      if (error) throw error;
      downloadBlob(data, fileName);
    } catch (error) {
      console.error("Erreur téléchargement CERFA:", error);
      toast.error("Erreur lors du téléchargement.");
    }
  };

  const handlePreviewGeneratedCerfa = async (storagePath, documentType) => {
    try {
      const bucket = documentType === 'CERFA_BUNDLE' ? 'cerfa-bundles' : 'generated-cerfas';
      const { data, error } = await supabase.storage.from(bucket).createSignedUrl(storagePath, 60);
      if (error || !data?.signedUrl) throw error || new Error("URL signée introuvable.");
      setPreviewUrl(data.signedUrl);
      setIsPreviewOpen(true);
    } catch (err) {
      toast.error("Impossible d'ouvrir l'aperçu.");
    }
  };

  const handleDownloadSignedCerfaBundle = async (storagePath, fileName) => {
    try {
      if (!storagePath || typeof storagePath !== 'string' || storagePath.trim() === '') {
        toast.error("Chemin du document manquant.");
        return;
      }
      const cleanStoragePath = storagePath.startsWith('/') ? storagePath.substring(1) : storagePath;
      const { data, error } = await supabase.storage.from(SIGNED_CERFA_BUNDLES_BUCKET).download(cleanStoragePath);
      if (error) throw error;
      if (!data) throw new Error("Aucune donnée reçue.");
      downloadBlob(data, fileName || 'dossier_cerfa_ticgn_signe.pdf');
    } catch (err) {
      console.error("Erreur téléchargement signé:", err);
      toast.error("Impossible de télécharger le dossier signé.");
    }
  };

  const handlePreviewSignedCerfaBundle = async (storagePathOfSignedBundle) => {
    try {
      const cleanStoragePath = storagePathOfSignedBundle.startsWith('/')
        ? storagePathOfSignedBundle.substring(1)
        : storagePathOfSignedBundle;
      const { data, error } = await supabase.storage.from(SIGNED_CERFA_BUNDLES_BUCKET).createSignedUrl(cleanStoragePath, 300);
      if (error || !data?.signedUrl) throw new Error("Impossible de créer l'URL d'aperçu.");
      setPreviewUrl(data.signedUrl);
      setIsPreviewOpen(true);
    } catch (err) {
      toast.error("Impossible d'ouvrir l'aperçu du dossier signé.");
    }
  };

  const confirmDeleteCerfaToast = ({ title, description, confirmLabel = "Supprimer" }) =>
    new Promise((resolve) => {
      toast.custom(
        (t) => (
          <div style={{ width: 320, background: "#fff", border: "1px solid rgba(0,0,0,.08)", borderRadius: 8, padding: "10px 12px", boxShadow: "0 4px 16px rgba(0,0,0,.10)", fontSize: 12 }}>
            <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 4 }}>{title}</div>
            {description ? (
              <div style={{ color: "rgba(0,0,0,.65)", fontSize: 11, marginBottom: 10, whiteSpace: "pre-line", lineHeight: 1.4 }}>{description}</div>
            ) : null}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 6 }}>
              <button onClick={() => { toast.dismiss(t.id); resolve(false); }} style={{ padding: "4px 10px", borderRadius: 6, border: "1px solid rgba(0,0,0,.12)", background: "#fff", fontSize: 11, cursor: "pointer" }}>Annuler</button>
              <button onClick={() => { toast.dismiss(t.id); resolve(true); }} style={{ padding: "4px 10px", borderRadius: 6, border: "none", background: "#0f766e", color: "#fff", fontWeight: 700, fontSize: 11, cursor: "pointer" }}>{confirmLabel}</button>
            </div>
          </div>
        ),
        { duration: Infinity, position: "top-center" }
      );
    });

  const handleDeleteGeneratedCerfa = async (cerfaDocId, storagePath, documentType) => {
    if (!cerfaDocId || !storagePath) {
      toast.error("Suppression annulée : informations manquantes.");
      return;
    }
    const cerfaToDelete = generatedCerfas.find((c) => c.id === cerfaDocId);
    const fileLabel = cerfaToDelete?.file_name ? `« ${cerfaToDelete.file_name} »` : "ce CERFA";

    const confirmed = await confirmDeleteCerfaToast({
      title: "Confirmer la suppression",
      description: `Vous êtes sur le point de supprimer :\n${fileLabel}\n\nCette action est définitive.`,
      confirmLabel: "Supprimer",
    });
    if (!confirmed) { toast("Suppression annulée.", { duration: 2000 }); return; }

    const toastId = toast.loading(`Suppression en cours…\n${fileLabel}`, { id: `del-${cerfaDocId}`, style: { whiteSpace: "pre-line", maxWidth: 520 } });

    try {
      const bucket = documentType === "CERFA_BUNDLE" ? "cerfa-bundles" : "generated-cerfas";
      const { error: storageError } = await supabase.storage.from(bucket).remove([storagePath]);
      if (storageError) throw new Error(storageError.message);

      const { data: deletedRows, error: dbError } = await supabase
        .from("generated_cerfa_documents")
        .delete()
        .match({ id: cerfaDocId, account_id: client.account_id })
        .select("id");
      if (dbError) throw new Error(dbError.message);
      if (!deletedRows || deletedRows.length !== 1) throw new Error(`Suppression incohérente.`);

      toast.success(`CERFA supprimé.\n${fileLabel}`, { id: toastId, style: { whiteSpace: "pre-line", maxWidth: 520 } });
      fetchGeneratedCerfas();
    } catch (error) {
      toast.error(`Suppression impossible : ${error.message}`, { id: toastId });
    }
  };

  // ===== FINALISATION =====
  const handleMarkDocsVerified = () => {
    if (!client || !accountId) return;
    const executeVerification = async () => {
      setIsMarkingVerificationComplete(true);
      const loadingToastId = toast.loading("Validation en cours...");
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token) throw new Error("Session admin invalide.");
        const response = await fetch(`/api/ticgn/admin/accounts/${accountId}/mark-docs-verified`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` },
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.message || `Erreur serveur ${response.status}.`);
        toast.success("Vérification terminée avec succès !", { id: loadingToastId });
        fetchClientData();
      } catch (error) {
        console.error("Erreur markDocsVerified:", error);
        toast.error(`Erreur : ${error.message}`, { id: loadingToastId });
      } finally {
        setIsMarkingVerificationComplete(false);
      }
    };

    toast.custom((t) => (
      <div className={`${t.visible ? 'animate-enter' : 'animate-leave'} max-w-md w-full bg-white shadow-lg rounded-lg pointer-events-auto flex ring-1 ring-black ring-opacity-5`}>
        <div className="flex-1 w-0 p-4">
          <div className="flex items-start">
            <div className="flex-shrink-0 pt-0.5"><FiAlertTriangle className="h-10 w-10 text-yellow-500 rounded-full bg-yellow-100 p-2" /></div>
            <div className="ml-3 flex-1">
              <p className="text-sm font-medium text-gray-900">Confirmer la vérification ?</p>
              <p className="mt-1 text-sm text-gray-500">Voulez-vous marquer les documents de <strong>{client.company_name}</strong> comme vérifiés ?</p>
            </div>
          </div>
        </div>
        <div className="flex border-l border-gray-200">
          <button onClick={() => { toast.dismiss(t.id); executeVerification(); }} className="w-full border border-transparent rounded-none rounded-r-lg p-4 flex items-center justify-center text-sm font-medium text-indigo-600 hover:text-indigo-500">Confirmer</button>
          <button onClick={() => toast.dismiss(t.id)} className="w-full border-t border-gray-200 rounded-none rounded-br-lg p-4 flex items-center justify-center text-sm font-medium text-gray-600 hover:text-gray-500">Annuler</button>
        </div>
      </div>
    ), { duration: Infinity, position: 'top-center' });
  };

  const handleConfirmCerfaGeneration = () => {
    if (!client || !accountId || !fiscalData?.exercice_end_date) {
      toast.error("Données manquantes pour confirmer la génération.");
      return;
    }
    const exerciceEndDateStr = new Date(fiscalData.exercice_end_date).toISOString().split('T')[0];
    const eraGenerated = generatedCerfas.some(cf => cf.document_type === 'CERFA_ERA_G');
    const ticvaGenerated = generatedCerfas.some(cf => cf.document_type === 'CERFA_TIC_VA_GC');
    const tic3310Generated = generatedCerfas.some(cf => cf.document_type === 'CERFA_3310_TIC');

    if (!eraGenerated || !ticvaGenerated || !tic3310Generated) {
      toast.error(
        <div>
          <p className="font-bold">Documents manquants !</p>
          <p className="text-sm">Veuillez générer ERA-G, TIC-VA-GC et 3310-TIC avant de valider.</p>
        </div>
      );
      return;
    }

    const executeConfirmation = async () => {
      setIsConfirmingCerfaGen(true);
      const toastId = toast.loading("Confirmation en cours...");
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token) throw new Error("Session admin invalide.");
        const response = await fetch(`/api/ticgn/admin/accounts/${accountId}/mark-cerfa-generation-complete`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` },
          body: JSON.stringify({ exercice_end_date: exerciceEndDateStr, fiscal_year: selectedYear })
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.message || `Erreur serveur ${response.status}`);
        toast.success("Génération des CERFA TICGN confirmée !", { id: toastId });
        const nowIso = new Date().toISOString();
        setDossierYear(prev => prev ? { ...prev, admin_cerfa_generation_completed_at: nowIso, updated_at: nowIso } : prev);
        prevWorkflowStepRef.current = 5;
        setOpenStepIndex(5);
        fetchClientData();
      } catch (error) {
        console.error("Erreur confirmCerfa:", error);
        toast.error(`Erreur : ${error.message}`, { id: toastId });
      } finally {
        setIsConfirmingCerfaGen(false);
      }
    };

    toast.custom((t) => (
      <div className={`${t.visible ? 'animate-enter' : 'animate-leave'} max-w-md w-full bg-white shadow-lg rounded-lg pointer-events-auto flex ring-1 ring-black ring-opacity-5`}>
        <div className="flex-1 w-0 p-4">
          <div className="flex items-start">
            <div className="flex-shrink-0 pt-0.5"><FiCheckCircle className="h-10 w-10 text-green-500 rounded-full bg-green-100 p-2" /></div>
            <div className="ml-3 flex-1">
              <p className="text-sm font-medium text-gray-900">Tout est correct ?</p>
              <p className="mt-1 text-sm text-gray-500">Vous confirmez que les 3 CERFA (ERA-G, TIC-VA-GC, 3310) sont générés et prêts pour la fusion ?</p>
            </div>
          </div>
        </div>
        <div className="flex border-l border-gray-200">
          <button onClick={() => { toast.dismiss(t.id); executeConfirmation(); }} className="w-full border border-transparent rounded-none rounded-r-lg p-4 flex items-center justify-center text-sm font-medium text-green-600 hover:text-green-500">Confirmer</button>
          <button onClick={() => toast.dismiss(t.id)} className="w-full border-t border-gray-200 rounded-none rounded-br-lg p-4 flex items-center justify-center text-sm font-medium text-gray-600 hover:text-gray-500">Annuler</button>
        </div>
      </div>
    ), { duration: Infinity, position: 'top-center' });
  };

  // ===== COMBINAISON CERFAS =====
  const handleCombineCerfas = () => {
    if (!client || !client.account_id || !generatedCerfas || generatedCerfas.length === 0) {
      toast.error("Données manquantes ou aucun CERFA généré.");
      return;
    }
    const exerciceEndDateStr = generatedCerfas[0].exercice_end_date;
    const cerfasDuDossier = generatedCerfas.filter(c =>
      c.exercice_end_date === exerciceEndDateStr &&
      ['CERFA_ERA_G', 'CERFA_TIC_VA_GC', 'CERFA_3310_TIC'].includes(c.document_type)
    );
    const cerfaPaths = cerfasDuDossier.map(c => c.storage_path);
    if (cerfaPaths.length < 3) {
      toast.error("Impossible de combiner : il manque des documents (ERA-G, TIC-VA-GC ou 3310).");
      return;
    }

    const executeCombination = async () => {
      setIsCombiningCerfas(true);
      const toastId = toast.loading("Fusion en cours...");
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const payload = {
          accountId: client.account_id,
          account_id: client.account_id,
          exercice_end_date: exerciceEndDateStr,
          cerfa_paths: cerfaPaths,
          fiscal_year: selectedYear,
          tax_type: 'TICGN',
        };
        const response = await fetch('/api/ticgn/documents/combine-cerfas', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
          body: JSON.stringify(payload)
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.error || result.message || "Erreur fusion");
        toast.success("Succès !", { id: toastId });
        await Promise.all([fetchGeneratedCerfas(), fetchClientData()]);
      } catch (error) {
        console.error("Erreur combinaison:", error);
        toast.error(`Erreur : ${error.message}`, { id: toastId });
      } finally {
        setIsCombiningCerfas(false);
      }
    };

    toast.custom((t) => (
      <div className={`${t.visible ? 'animate-enter' : 'animate-leave'} max-w-md w-full bg-white shadow-lg rounded-lg pointer-events-auto flex ring-1 ring-black ring-opacity-5`}>
        <div className="flex-1 w-0 p-4">
          <div className="flex items-start">
            <div className="flex-shrink-0 pt-0.5"><FiLayers className="h-10 w-10 text-indigo-500 rounded-full bg-indigo-100 p-2" /></div>
            <div className="ml-3 flex-1">
              <p className="text-sm font-medium text-gray-900">Fusionner le dossier TICGN ?</p>
              <p className="mt-1 text-sm text-gray-500">Cela va créer un fichier PDF unique contenant tous les CERFA TICGN, prêt pour la signature.</p>
            </div>
          </div>
        </div>
        <div className="flex border-l border-gray-200">
          <button onClick={() => { toast.dismiss(t.id); executeCombination(); }} className="w-full border border-transparent rounded-none rounded-r-lg p-4 flex items-center justify-center text-sm font-medium text-indigo-600 hover:text-indigo-500">Fusionner</button>
          <button onClick={() => toast.dismiss(t.id)} className="w-full border-t border-gray-200 rounded-none rounded-br-lg p-4 flex items-center justify-center text-sm font-medium text-gray-600 hover:text-gray-500">Annuler</button>
        </div>
      </div>
    ), { duration: Infinity, position: 'top-center' });
  };

  // ===== DELETE BUNDLE =====
  const handleDeleteCerfaBundle = () => {
    if (!client || !dossierYear?.cerfa_bundle_storage_path) return;
    const executeDeletion = async () => {
      const toastId = toast.loading("Suppression du dossier en cours...");
      try {
        const token = (await supabase.auth.getSession()).data.session?.access_token;
        if (!token) throw new Error("Session invalide.");
        const response = await fetch(`/api/ticgn/admin/accounts/${client.account_id}/delete-cerfa-bundle?fiscal_year=${selectedYear}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` },
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.message || 'Erreur suppression.');
        toast.success("Dossier combiné TICGN supprimé.", { id: toastId });
        await fetchClientData();
      } catch (error) {
        console.error("Erreur delete:", error);
        toast.error(`Erreur: ${error.message}`, { id: toastId });
      }
    };

    toast.custom((t) => (
      <div className={`${t.visible ? 'animate-enter' : 'animate-leave'} max-w-md w-full bg-white shadow-lg rounded-lg pointer-events-auto flex ring-1 ring-black ring-opacity-5`}>
        <div className="flex-1 w-0 p-4">
          <div className="flex items-start">
            <div className="flex-shrink-0 pt-0.5"><FiTrash2 className="h-10 w-10 text-red-500 rounded-full bg-red-100 p-2" /></div>
            <div className="ml-3 flex-1">
              <p className="text-sm font-medium text-gray-900">Supprimer le dossier combiné TICGN ?</p>
              <p className="mt-1 text-sm text-gray-500">Attention, cela supprimera le fichier PDF unique. Cette action est irréversible.</p>
            </div>
          </div>
        </div>
        <div className="flex border-l border-gray-200">
          <button onClick={() => { toast.dismiss(t.id); executeDeletion(); }} className="w-full border border-transparent rounded-none rounded-r-lg p-4 flex items-center justify-center text-sm font-medium text-red-600 hover:text-red-500">Supprimer</button>
          <button onClick={() => toast.dismiss(t.id)} className="w-full border-t border-gray-200 rounded-none rounded-br-lg p-4 flex items-center justify-center text-sm font-medium text-gray-600 hover:text-gray-500">Annuler</button>
        </div>
      </div>
    ), { duration: Infinity, position: 'top-center' });
  };

  // ===== YOUSIGN =====
  const handleSendYousignRequest = () => {
    if (!client || !dossierYear?.cerfa_bundle_storage_path) {
      toast.error("Le dossier PDF combiné doit être généré avant l'envoi.");
      return;
    }
    const executeSending = async () => {
      setIsNotifying(true);
      const toastId = toast.loading("Envoi vers Yousign en cours...");
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const response = await fetch(`/api/ticgn/admin/accounts/${accountId}/send-yousign-cerfa`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
          body: JSON.stringify({ fiscal_year: selectedYear })
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.message || "Erreur serveur Yousign");
        toast.success(
          <div><span className="font-bold">Succès !</span><br/><span className="text-sm">Le client a reçu l'email de signature.</span></div>,
          { id: toastId, duration: 5000 }
        );
        await fetchClientData();
      } catch (error) {
        console.error("Erreur Yousign:", error);
        toast.error(`Erreur : ${error.message}`, { id: toastId });
      } finally {
        setIsNotifying(false);
      }
    };

    toast.custom((t) => (
      <div className={`${t.visible ? 'animate-enter' : 'animate-leave'} max-w-md w-full bg-white shadow-lg rounded-lg pointer-events-auto flex ring-1 ring-black ring-opacity-5`}>
        <div className="flex-1 w-0 p-4">
          <div className="flex items-start">
            <div className="flex-shrink-0 pt-0.5"><FiPenTool className="h-10 w-10 text-purple-500 rounded-full bg-purple-100 p-2" /></div>
            <div className="ml-3 flex-1">
              <p className="text-sm font-medium text-gray-900">Lancer la signature TICGN ?</p>
              <p className="mt-1 text-sm text-gray-500">Vous allez envoyer le dossier TICGN complet par email à <strong>{client.company_name}</strong> via Yousign.</p>
            </div>
          </div>
        </div>
        <div className="flex border-l border-gray-200">
          <button onClick={() => { toast.dismiss(t.id); executeSending(); }} className="w-full border border-transparent rounded-none rounded-r-lg p-4 flex items-center justify-center text-sm font-medium text-purple-600 hover:text-purple-500">Envoyer</button>
          <button onClick={() => toast.dismiss(t.id)} className="w-full border-t border-gray-200 rounded-none rounded-br-lg p-4 flex items-center justify-center text-sm font-medium text-gray-600 hover:text-gray-500">Annuler</button>
        </div>
      </div>
    ), { duration: Infinity, position: 'top-center' });
  };

  const handleRetrySignature = () => {
    if (!client || !dossierYear?.cerfa_bundle_storage_path) {
      toast.error("Le dossier PDF combiné doit être généré avant la relance.");
      return;
    }
    const executeRetry = async () => {
      setIsNotifying(true);
      const toastId = toast.loading("Relance Yousign en cours...");
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const response = await fetch(`/api/ticgn/admin/accounts/${accountId}/send-yousign-cerfa`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
          body: JSON.stringify({ fiscal_year: selectedYear })
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.message || "Erreur serveur Yousign");
        toast.success(
          <div><span className="font-bold">Relance envoyée !</span><br/><span className="text-sm">Le client va recevoir un nouvel email de signature.</span></div>,
          { id: toastId, duration: 5000 }
        );
        await fetchClientData();
      } catch (error) {
        console.error("Erreur relance Yousign:", error);
        toast.error(`Erreur : ${error.message}`, { id: toastId });
      } finally {
        setIsNotifying(false);
      }
    };

    toast.custom((t) => (
      <div className={`${t.visible ? 'animate-enter' : 'animate-leave'} max-w-md w-full bg-white shadow-lg rounded-lg pointer-events-auto flex ring-1 ring-black ring-opacity-5`}>
        <div className="flex-1 w-0 p-4">
          <div className="flex items-start">
            <div className="flex-shrink-0 pt-0.5"><FiRefreshCw className="h-10 w-10 text-yellow-500 rounded-full bg-yellow-100 p-2" /></div>
            <div className="ml-3 flex-1">
              <p className="text-sm font-medium text-gray-900">Relancer la signature TICGN ?</p>
              <p className="mt-1 text-sm text-gray-500">Cela <strong>annulera la demande actuelle</strong> et enverra un nouvel email.</p>
            </div>
          </div>
        </div>
        <div className="flex border-l border-gray-200">
          <button onClick={() => { toast.dismiss(t.id); executeRetry(); }} className="w-full border border-transparent rounded-none rounded-r-lg p-4 flex items-center justify-center text-sm font-medium text-yellow-600 hover:text-yellow-500">Oui, relancer</button>
          <button onClick={() => toast.dismiss(t.id)} className="w-full border-t border-gray-200 rounded-none rounded-br-lg p-4 flex items-center justify-center text-sm font-medium text-gray-600 hover:text-gray-500">Annuler</button>
        </div>
      </div>
    ), { duration: Infinity, position: 'top-center' });
  };

  const handleCheckStatus = async () => {
    const toastId = toast.loading("Vérification auprès de Yousign...");
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const response = await fetch(
        `/api/ticgn/admin/accounts/${accountId}/check-yousign-status?fiscal_year=${selectedYear}`,
        { method: 'GET', headers: { 'Authorization': `Bearer ${session.access_token}` } }
      );
      const result = await response.json();
      if (result.success) {
        if (result.status === 'done') {
          toast.success("C'est signé ! Dossier TICGN mis à jour.", { id: toastId });
          fetchClientData();
        } else {
          toast("Statut actuel : " + result.status, { id: toastId, icon: '⏳' });
        }
      } else {
        throw new Error(result.message);
      }
    } catch (error) {
      toast.error("Erreur : " + error.message, { id: toastId });
    }
  };

  // ===== LOGIQUES CONDITIONNELLES =====
  const liasseOcrTraitee = fiscalData != null && fiscalData.cvae_reference_turnover != null;

  const cerfaEraGGenerePourExercice = useMemo(() => {
    if (!selectedYear || !generatedCerfas) return false;
    return generatedCerfas.some(cf => cf.document_type === 'CERFA_ERA_G');
  }, [selectedYear, generatedCerfas]);

  const cerfaTicVaGcGenerePourExercice = useMemo(() => {
    if (!selectedYear || !generatedCerfas) return false;
    return generatedCerfas.some(cf => cf.document_type === 'CERFA_TIC_VA_GC');
  }, [selectedYear, generatedCerfas]);

  const cerfa3310TicGenerePourPeriode = useMemo(() => {
    if (!selectedYear || !generatedCerfas) return false;
    return generatedCerfas.some(cf => cf.document_type === 'CERFA_3310_TIC');
  }, [selectedYear, generatedCerfas]);

  const allIndividualCerfasGenerated = cerfaEraGGenerePourExercice && cerfaTicVaGcGenerePourExercice && cerfa3310TicGenerePourPeriode;

  const groupedDocuments = useMemo(() => {
    const groups = { 'documents_financiers': [] };
    (allUserDocuments || []).forEach(doc => {
      if (
        doc.document_type_id === 'documents_financiers' &&
        Number(doc.year) === selectedYear
      ) {
        groups['documents_financiers'].push(doc);
      }
    });
    return groups;
  }, [allUserDocuments, selectedYear]);

  const isLiasseValidated = useMemo(() => {
    const financialDocs = groupedDocuments['documents_financiers'] || [];
    if (financialDocs.length === 0) return false;
    return financialDocs.some(doc =>
      doc.status === 'Validé manuellement' || doc.status === 'Validé'
    );
  }, [groupedDocuments]);

  const invoicesCountByPceYear = useMemo(() => {
    const map = new Map();
    (allUserDocuments || []).forEach((doc) => {
      if (doc.document_type_id !== 'factures_gaz') return;
      if (!doc.pce_id || !doc.year) return;
      const key = `${doc.pce_id}:${doc.year}`;
      map.set(key, (map.get(key) || 0) + 1);
    });
    return map;
  }, [allUserDocuments]);

  const hasInvoicesForPceYear = useCallback((pceId, year) => {
    return (invoicesCountByPceYear.get(`${pceId}:${year}`) || 0) > 0;
  }, [invoicesCountByPceYear]);

  const hasManualLinesForPce = useCallback((pceId) => {
    return (manualLinesCountByPce[pceId] || 0) > 0;
  }, [manualLinesCountByPce]);

  // Nouvel ordre : Liasse → TIC VA-GC → ERA-G → 3310
  // Gaz-intensité : null = VA négative (réputée > seuil → OK), >= 0.5 → OK
  const gazIntensiteValidee = gazIntensite === null || (typeof gazIntensite === 'number' && gazIntensite >= 0.5);
  const gazIntensiteInsuffisante = cerfaTicVaGcGenerePourExercice && !gazIntensiteValidee;

  // Tous les PCEs avec données doivent avoir leur ERA-G
  const tousEraGGeneres = useMemo(() => {
    if (!selectedYear || !generatedCerfas || !pceList || pceList.length === 0) return false;
    const eraCerfas = generatedCerfas.filter(cf => cf.document_type === 'CERFA_ERA_G');
    const pcesAvecData = pceList.filter(pce => {
      const hasInvoices = (invoicesCountByPceYear.get(`${pce.id}:${selectedYear}`) || 0) > 0;
      const hasManual = (manualLinesCountByPce[pce.id] || 0) > 0;
      return hasInvoices || hasManual;
    });
    if (pcesAvecData.length === 0) return false;
    return pcesAvecData.every(pce => eraCerfas.some(cf => (cf.pce_id === pce.id) || (cf.pdl_id === pce.id)));
  }, [selectedYear, generatedCerfas, pceList, invoicesCountByPceYear, manualLinesCountByPce]);

  const disableGenerateTicVaGcButton = isGeneratingTICVAGC || cerfaTicVaGcGenerePourExercice || !isLiasseValidated || !fiscalData?.id;
  const disableGenerateEraGButton = generatingPceId !== null || !cerfaTicVaGcGenerePourExercice || !isLiasseValidated || gazIntensiteInsuffisante;
  const disableGenerate3310TicButton = isGenerating3310TIC || cerfa3310TicGenerePourPeriode || !tousEraGGeneres || gazIntensiteInsuffisante;

  // Nouvel ordre : 0=Liasse → 1=TIC VA-GC → 2=ERA-G → 3=3310 → 4=Finalisation → 5=Combinaison → 6=Signature
  const currentWorkflowStep = useMemo(() => {
    if (!dossierYear) {
      if (cerfa3310TicGenerePourPeriode)                return 4;
      if (cerfaEraGGenerePourExercice)                  return 3;
      if (cerfaTicVaGcGenerePourExercice)               return 2;
      if (liasseOcrTraitee && isLiasseValidated)        return 1;
      return 0;
    }
    if (dossierYear.cerfa_bundle_generated_at)           return 6;
    if (dossierYear.admin_cerfa_generation_completed_at) return 5;
    if (cerfa3310TicGenerePourPeriode)                   return 4;
    if (cerfaEraGGenerePourExercice)                     return 3;
    if (cerfaTicVaGcGenerePourExercice)                  return 2;
    if (liasseOcrTraitee && isLiasseValidated)           return 1;
    return 0;
  }, [
    dossierYear,
    cerfaEraGGenerePourExercice,
    liasseOcrTraitee,
    isLiasseValidated,
    cerfaTicVaGcGenerePourExercice,
    cerfa3310TicGenerePourPeriode,
  ]);

  const prevAccountIdRef = useRef(accountId);
  const prevSelectedYearRef = useRef(selectedYear);
  useEffect(() => {
    if (!accountId) return;
    const accountChanged = prevAccountIdRef.current !== accountId;
    const yearChanged = prevSelectedYearRef.current !== selectedYear;
    prevAccountIdRef.current = accountId;
    prevSelectedYearRef.current = selectedYear;
    if (accountChanged || yearChanged) {
      prevWorkflowStepRef.current = null;
      setOpenStepIndex(0);
    }
  }, [accountId, selectedYear]);

  // ===== CHARGEMENT DES LIGNES MANUELLES ET FLAGS MODE MANUEL =====
  const fetchManualLinesCounts = useCallback(async () => {
    if (!accountId || !selectedYear) return;
    try {
      const { data, error } = await supabase
        .from('ticgn_manual_lines')
        .select('pce_id')
        .eq('account_id', accountId)
        .eq('year', selectedYear);

      if (error) throw error;

      const counts = (data || []).reduce((acc, row) => {
        if (row.pce_id) acc[row.pce_id] = (acc[row.pce_id] || 0) + 1;
        return acc;
      }, {});
      setManualLinesCountByPce(counts);
    } catch (err) {
      console.error('[AnalyseTicgn] Erreur chargement lignes manuelles:', err);
    }
  }, [accountId, selectedYear]);

  const fetchManualModeFlags = useCallback(async () => {
    if (!accountId || !selectedYear) return;
    try {
      const { data, error } = await supabase
        .from('ticgn_manual_mode')
        .select('pce_id, enabled')
        .eq('account_id', accountId)
        .eq('year', selectedYear);

      if (error) throw error;

      const flags = (data || []).reduce((acc, row) => {
        if (row.pce_id && row.enabled) acc[row.pce_id] = true;
        return acc;
      }, {});
      setManualModeFlags(flags);
    } catch (err) {
      console.error('[AnalyseTicgn] Erreur chargement mode manuel:', err);
    }
  }, [accountId, selectedYear]);

  // Chargement option exonération + pourcentage depuis ticgn_dossier_years
  const fetchExonerationOption = useCallback(async () => {
    if (!accountId || !selectedYear) return;
    try {
      const { data, error } = await supabase
        .from('ticgn_dossier_years')
        .select('exoneration_option, pourcentage_exonere')
        .eq('account_id', accountId)
        .eq('fiscal_year', selectedYear)
        .maybeSingle();

      if (error) throw error;
      setExonerationOption(data?.exoneration_option || 'J');
      setPourcentageExonere(data?.pourcentage_exonere ?? 100);
    } catch (err) {
      console.error('[AnalyseTicgn] Erreur chargement exoneration:', err);
    }
  }, [accountId, selectedYear]);

  const saveExonerationOption = useCallback(async (option) => {
    if (!accountId || !selectedYear) return;
    setExonerationOption(option);
    try {
      const { error } = await supabase
        .from('ticgn_dossier_years')
        .upsert(
          { account_id: accountId, fiscal_year: selectedYear, exoneration_option: option, updated_at: new Date().toISOString() },
          { onConflict: 'account_id,fiscal_year' }
        );
      if (error) throw error;
    } catch (err) {
      console.error('[AnalyseTicgn] Erreur sauvegarde exoneration:', err);
    }
  }, [accountId, selectedYear]);

  const savePourcentageExonere = useCallback(async (pct) => {
    if (!accountId || !selectedYear) return;
    const val = Math.min(100, Math.max(0, parseFloat(pct) || 100));
    setPourcentageExonere(val);
    try {
      const { error } = await supabase
        .from('ticgn_dossier_years')
        .upsert(
          { account_id: accountId, fiscal_year: selectedYear, pourcentage_exonere: val, updated_at: new Date().toISOString() },
          { onConflict: 'account_id,fiscal_year' }
        );
      if (error) throw error;
    } catch (err) {
      console.error('[AnalyseTicgn] Erreur sauvegarde pourcentage:', err);
    }
  }, [accountId, selectedYear]);

  useEffect(() => {
    if (accountId && selectedYear) {
      fetchManualLinesCounts();
      fetchManualModeFlags();
      fetchExonerationOption();
    }
  }, [accountId, selectedYear, fetchManualLinesCounts, fetchManualModeFlags, fetchExonerationOption]);

  // ===== AUTO-ADVANCE avec support mode manuel =====
  const arrivedFromManualMode = !location.state?.analysisResult;
  const manualModeLockRef = useRef(arrivedFromManualMode);
  useEffect(() => {
    if (!accountId) return;
    if (prevWorkflowStepRef.current === null) {
      prevWorkflowStepRef.current = currentWorkflowStep;
      if (manualModeLockRef.current) {
        setOpenStepIndex(0);
      } else {
        setOpenStepIndex(currentWorkflowStep);
      }
      return;
    }
    if (!manualModeLockRef.current && currentWorkflowStep > prevWorkflowStepRef.current) {
      setOpenStepIndex(currentWorkflowStep);
    }
    prevWorkflowStepRef.current = currentWorkflowStep;
  }, [accountId, currentWorkflowStep]);

  // ===== LOADING =====
  if (loading) {
    return (
      <div className="h-screen flex justify-center items-center">
        <div className="text-center space-y-3">
          <FiLoader className="animate-spin h-8 w-8 text-teal-600 mx-auto" />
          <p className="text-gray-600 text-sm font-medium">Chargement des analyses TICGN...</p>
        </div>
      </div>
    );
  }

  // ===== VUE GLOBALE (page d'accueil TICGN) =====
  if (!accountId) {
    return (
      <div className="min-h-screen bg-gray-50 p-3">
        <div className="max-w-6xl mx-auto space-y-2">
          <div className="flex items-center gap-2 justify-end">
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(parseInt(e.target.value, 10))}
              className="py-1 px-1.5 border border-gray-200 rounded text-[10px] bg-white"
            >
              <option value={0}>Tous les mois</option>
              {Array.from({ length: 12 }).map((_, i) => (
                <option key={i + 1} value={i + 1}>{MOIS[i]}</option>
              ))}
            </select>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}
              className="py-1 px-1.5 border border-gray-200 rounded text-[10px] bg-white"
            >
              {[2023, 2024, 2025, 2026, 2027].map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
          <SignedContractsTICGN
            selectedYear={selectedYear}
            selectedMonth={selectedMonth}
            session={session}
            onSelectClient={(contract) => {
              navigate(`/dashboard/admin/analyse-ticgn/${contract.account_id}`, {
                state: { year: selectedYear, month: selectedMonth },
              });
            }}
          />
        </div>
      </div>
    );
  }

  // ===== VUE DÉTAILLÉE — Client non trouvé =====
  if (!client) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center bg-white rounded-xl p-8 shadow-lg">
          <FiBarChart2 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-bold text-gray-900 mb-3">Client non trouvé</h3>
          <p className="text-gray-600 text-sm">Aucune donnée disponible pour ce client.</p>
          <Link
            to="/dashboard/admin/analyse-ticgn"
            className="inline-flex items-center mt-4 px-4 py-2 bg-teal-600 text-white font-medium rounded-lg hover:bg-teal-700 transition-colors text-sm"
          >
            <FiArrowLeft className="h-4 w-4 mr-1.5" />
            Retour à la liste
          </Link>
        </div>
      </div>
    );
  }

  // ===== VUE DÉTAILLÉE — Analyse TICGN =====
  return (
    <div className="h-screen flex flex-col bg-gray-50">
      <CompactHeader
        client={client}
        loading={loading}
        onRefresh={() => fetchClientData()}
        selectedYear={selectedYear}
        onEditRaisonSociale={() => setIsRaisonSocialeModalOpen(true)}
      />

      <TabSystem activeTab={activeTab} onTabChange={setActiveTab} />

      {success && (
        <div className="bg-green-50 p-2 mx-2 mt-2 rounded text-xs">
          <p className="text-green-800">{success}</p>
        </div>
      )}
      {errorMessage && (
        <div className="bg-red-50 p-2 mx-2 mt-2 rounded text-xs">
          <p className="text-red-800">{errorMessage}</p>
        </div>
      )}

      <div className="flex-1 overflow-hidden bg-white">
        {activeTab === 'analyse' && (
          <div className="h-full overflow-y-auto p-2 space-y-2">

            {/* Année fiscale */}
            <div className="bg-white rounded-md shadow-sm border border-gray-200 p-2">
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs font-semibold text-slate-700">Année fiscale de travail</p>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}
                  className="px-2 py-1 bg-teal-50 text-teal-800 text-xs font-bold rounded-md border border-teal-200"
                >
                  {[2023, 2024, 2025, 2026, 2027].map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* 1. Import/OCR/Validation Liasse Fiscale */}
            <WorkflowStepSection
              stepIndex={0}
              title="Étape 1 : Import et Traitement Liasse Fiscale"
              subtitle="Upload, OCR et validation des données fiscales."
              icon={FiUpload}
              isActive={openStepIndex === 0}
              isOpen={openStepIndex === 0}
              onToggle={() => setOpenStepIndex(openStepIndex === 0 ? null : 0)}
            >
              <DocumentCategorySection
                key="documents_financiers"
                categoryKey="documents_financiers"
                categoryName="Documents financiers (Liasse fiscale)"
                documents={groupedDocuments['documents_financiers'] || []}
                onValidateAllInCategory={handleValidateAllInCategory}
                onProcessLiasse={handleProcessLiasse}
              >
                {(groupedDocuments['documents_financiers'] || []).length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-slate-200">
                      <tbody className="bg-white divide-y divide-slate-200">
                        {(groupedDocuments['documents_financiers'] || []).map(doc => (
                          <tr key={doc.id}>
                            <td className="px-3 py-2 whitespace-nowrap">
                              <div className="text-xs font-medium text-slate-900">{doc.original_file_name}</div>
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap">
                              <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${STATUS_DISPLAY[doc.status]?.color || 'bg-gray-100'}`}>
                                {doc.status}
                              </span>
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-right text-xs font-medium">
                              <div className="flex items-center justify-end gap-2">
                                <button
                                  onClick={() => handleDeleteLiasse(doc)}
                                  className="p-1.5 rounded text-red-500 hover:text-red-700 hover:bg-red-50 transition-colors"
                                  title="Supprimer cette liasse"
                                >
                                  <FiTrash2 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="p-4">
                    <FileUploadZone onFileUpload={handleLiasseUpload} />
                  </div>
                )}
              </DocumentCategorySection>
            </WorkflowStepSection>

            {/* 2. CERFA TIC-VA-GC */}
            <WorkflowStepSection
              stepIndex={1}
              title="Étape 2 : Calcul TIC-VA-GC (Gaz-intensité)"
              subtitle="Génération du CERFA 2040-TIC-VA-GC-SD."
              icon={FiDownloadCloud}
              isActive={openStepIndex === 1}
              isOpen={openStepIndex === 1}
              onToggle={() => setOpenStepIndex(openStepIndex === 1 ? null : 1)}
            >
              <div className="bg-sky-50 border-l-4 border-sky-400 p-2 mb-3">
                <p className="text-xs text-sky-700">
                  <FiAlertTriangle className="inline h-3 w-3 mr-1 align-text-top" />
                  Nécessite que les données de la liasse fiscale aient été extraites et validées.
                </p>
              </div>

              {!liasseOcrTraitee && (
                <div className="bg-orange-50 border-l-4 border-orange-400 p-2 mb-3">
                  <p className="text-xs text-orange-700">
                    L'analyse (OCR) de la liasse fiscale doit être effectuée avant de générer le CERFA TIC-VA-GC.
                  </p>
                </div>
              )}

              <div className="flex items-center justify-between">
                <button
                  onClick={handleGenerateCerfaTICVAGC}
                  disabled={disableGenerateTicVaGcButton}
                  className={`w-full sm:w-auto inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded shadow-sm text-white ${disableGenerateTicVaGcButton ? 'bg-slate-400 cursor-not-allowed' : 'bg-teal-600 hover:bg-teal-700'}`}
                  title={disableGenerateTicVaGcButton ? "Prérequis manquants ou CERFA déjà généré." : "Générer le CERFA TIC-VA-GC"}
                >
                  {isGeneratingTICVAGC ? <FiLoader className="animate-spin h-4 w-4 mr-1.5" /> : <FiFileText className="h-4 w-4 mr-1.5" />}
                  {isGeneratingTICVAGC ? 'Génération...' : cerfaTicVaGcGenerePourExercice ? 'CERFA (TIC-VA-GC) Généré' : 'Générer CERFA (TIC-VA-GC)'}
                </button>

                {cerfaTicVaGcGenerePourExercice && (
                  <div className={`ml-4 px-4 py-2 border-2 rounded-lg text-center ${
                    gazIntensiteValidee
                      ? 'bg-green-50 border-green-300'
                      : 'bg-red-50 border-red-300'
                  }`}>
                    <div className={`text-[10px] uppercase tracking-wide font-medium ${gazIntensiteValidee ? 'text-green-600' : 'text-red-600'}`}>Gaz-intensité</div>
                    <div className={`text-xl font-bold ${gazIntensiteValidee ? 'text-green-700' : 'text-red-700'}`}>
                      {gazIntensite === null ? '> seuil' : typeof gazIntensite === 'number' ? `${gazIntensite.toFixed(2)} %` : '—'}
                    </div>
                    {!gazIntensiteValidee && (
                      <div className="text-[10px] text-red-500 mt-0.5">Seuil requis : 0,50 %</div>
                    )}
                  </div>
                )}
              </div>
            </WorkflowStepSection>

            {/* 3. CERFA ERA-G (par PCE) */}
            <WorkflowStepSection
              stepIndex={2}
              title="Étape 3 : Génération des CERFA ERA-G (par PCE)"
              subtitle="Générez un document 2040-TIC-ERA-G-SD pour chaque PCE."
              icon={FiFileText}
              isActive={openStepIndex === 2}
              isOpen={openStepIndex === 2}
              onToggle={() => setOpenStepIndex(openStepIndex === 2 ? null : 2)}
            >
              {(!isLiasseValidated || !cerfaTicVaGcGenerePourExercice) && (
                <div className="bg-orange-50 border-l-4 border-orange-400 p-2 mb-3">
                  <p className="text-xs text-orange-700">
                    <FiAlertTriangle className="inline h-3 w-3 mr-1 align-text-top" />
                    Les étapes 1 (Liasse fiscale) et 2 (TIC-VA-GC) doivent être complétées avant de générer les ERA-G.
                  </p>
                </div>
              )}

              <div className="space-y-2">
                {/* Selecteur exoneration + pourcentage */}
                <div className="flex flex-wrap items-center gap-3 px-3 py-2 bg-teal-50 border border-teal-200 rounded-lg">
                  <span className="text-xs font-semibold text-teal-800">Exonération :</span>
                  <select
                    value={exonerationOption}
                    onChange={(e) => saveExonerationOption(e.target.value)}
                    className="text-xs border border-teal-300 rounded px-2 py-1 bg-white text-teal-800 font-medium cursor-pointer"
                  >
                    <option value="J">G11 — Installation intensive exposée à la concurrence internationale</option>
                    <option value="K">G12 — Déshydratation de légumes et plantes aromatiques</option>
                    <option value="O">G17 — Travaux agricoles et forestiers</option>
                  </select>
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-teal-700">% exonéré :</span>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={pourcentageExonere}
                      onChange={(e) => savePourcentageExonere(e.target.value)}
                      className="w-16 text-xs border border-teal-300 rounded px-2 py-1 bg-white text-teal-800 font-medium text-center"
                    />
                    <span className="text-xs text-teal-600">%</span>
                  </div>
                </div>

                {pceList.length > 0 ? (
                  pceList.map((pce) => {
                    const hasDocsForYear = hasInvoicesForPceYear(pce.id, selectedYear);
                    const hasManualLines = hasManualLinesForPce(pce.id);
                    const isManualMode = !!manualModeFlags[pce.id];
                    const canGenerate = (hasDocsForYear || hasManualLines) && !disableGenerateEraGButton;

                    const isCerfaGenerated = generatedCerfas.some((cerfa) => {
                      if (!cerfa.pce_id && !cerfa.pdl_id) return false;
                      const pceMatch = (cerfa.pce_id === pce.id) || (cerfa.pdl_id === pce.id);
                      const yearFromName = cerfa.file_name?.match(/_(\d{4})(?:_|\.pdf)/)?.[1];
                      const yearMatch = yearFromName ? parseInt(yearFromName, 10) === selectedYear : false;
                      return pceMatch && yearMatch;
                    });

                    return (
                      <div key={pce.id} className={`p-2 rounded-md border ${isManualMode && !hasDocsForYear ? 'bg-purple-50 border-purple-200' : 'bg-slate-50'}`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center flex-wrap gap-1">
                            <span className="font-medium text-gray-800 text-xs">{pce.site_name || 'Site inconnu'}</span>
                            <span className="ml-2 text-[10px] text-gray-500 font-mono">PCE: {pce.pce_number || '—'}</span>
                            {isCerfaGenerated ? (
                              <span className="ml-3 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                <FiCheck className="mr-1 h-3 w-3" /> Généré
                              </span>
                            ) : hasDocsForYear ? (
                              <span className="ml-3 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                <FiClock className="mr-1 h-3 w-3" /> À générer
                              </span>
                            ) : hasManualLines ? (
                              <span className="ml-3 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                <FiEdit3 className="mr-1 h-3 w-3" /> {manualLinesCountByPce[pce.id]} ligne(s) manuelle(s)
                              </span>
                            ) : isManualMode ? (
                              <span className="ml-3 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                <FiEdit3 className="mr-1 h-3 w-3" /> Mode manuel — cliquez sur le crayon pour saisir
                              </span>
                            ) : (
                              <span className="ml-3 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-200 text-slate-700">
                                <FiX className="mr-1 h-3 w-3" /> Aucune facture gaz {selectedYear}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => openSiretEditor(pce)}
                              className={`p-2 rounded hover:bg-slate-200/60 transition-colors ${
                                pce.siret_etablissement
                                  ? 'text-slate-600 hover:text-slate-900'
                                  : 'text-orange-500 hover:text-orange-700'
                              }`}
                              title={
                                pce.siret_etablissement
                                  ? `SIRET établissement : ${pce.siret_etablissement}${pce.naf_code ? ' · APE ' + pce.naf_code : ''}`
                                  : 'Définir le SIRET de l\'établissement (utilisera le siège par défaut)'
                              }
                              aria-label="Éditer SIRET établissement et code APE"
                            >
                              <FiSettings className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => openTicgnEditor(pce)}
                              className="p-2 rounded hover:bg-slate-200/60 text-slate-600 hover:text-slate-900 transition-colors"
                              title={hasManualLines ? 'Modifier les lignes manuelles' : 'Corriger les champs factures (OCR) ou saisir manuellement'}
                              aria-label="Corriger les champs factures ou saisir manuellement"
                            >
                              <FiEdit3 className="h-4 w-4" />
                            </button>
                            {isCerfaGenerated ? (
                              <span className="text-xs font-medium text-gray-500">Déjà traité pour {selectedYear}</span>
                            ) : !hasDocsForYear && !hasManualLines ? (
                              <button
                                onClick={() => navigate(`/dashboard/admin/client/${client?.id || accountId}`, { state: { activeTab: 'ticgn' } })}
                                className="inline-flex items-center justify-center px-3 py-1 text-xs font-medium rounded text-white shadow-sm bg-orange-500 hover:bg-orange-600"
                                title={`Ajouter des factures gaz pour ${pce.site_name}`}
                              >
                                <FiUpload className="h-3 w-3 mr-1.5" />
                                Ajouter factures
                              </button>
                            ) : (
                              <button
                                onClick={() => handleGenerateCerfaERAG(pce)}
                                disabled={!canGenerate}
                                className={`inline-flex items-center justify-center px-3 py-1 text-xs font-medium rounded text-white shadow-sm ${
                                  !canGenerate ? 'bg-slate-300 cursor-not-allowed' : hasManualLines && !hasDocsForYear ? 'bg-purple-600 hover:bg-purple-700' : 'bg-teal-600 hover:bg-teal-700'
                                }`}
                                title={
                                  !isLiasseValidated || !cerfaTicVaGcGenerePourExercice
                                    ? 'Complétez les étapes 1 et 2 d\'abord'
                                    : `Générer le CERFA ERA-G pour ${pce.site_name}`
                                }
                              >
                                {generatingPceId === pce.id ? (
                                  <FiLoader className="animate-spin h-3 w-3 mr-1.5" />
                                ) : (
                                  <FiCpu className="h-3 w-3 mr-1.5" />
                                )}
                                {generatingPceId === pce.id ? 'En cours...' : 'Générer'}
                              </button>
                            )}
                          </div>
                        </div>
                        <div className="mt-2 px-1">
                          <button
                            type="button"
                            role="switch"
                            aria-checked={!!premierBilanConfig[`${pce.id}-${selectedYear}`]?.enabled}
                            onClick={() => {
                              const key = `${pce.id}-${selectedYear}`;
                              const next = !premierBilanConfig[key]?.enabled;
                              setPremierBilanConfig((prev) => ({
                                ...prev,
                                [key]: {
                                  ...prev[key],
                                  enabled: next,
                                  startDate: next ? prev[key]?.startDate || '' : '',
                                },
                              }));
                            }}
                            className="flex items-center gap-2 w-fit"
                          >
                            <span
                              className={`relative inline-flex h-4 w-7 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out ${
                                premierBilanConfig[`${pce.id}-${selectedYear}`]?.enabled ? 'bg-amber-500' : 'bg-gray-300'
                              }`}
                            >
                              <span
                                className={`pointer-events-none inline-block h-3 w-3 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                  premierBilanConfig[`${pce.id}-${selectedYear}`]?.enabled ? 'translate-x-3' : 'translate-x-0'
                                }`}
                              />
                            </span>
                            <span className={`text-xs font-medium ${premierBilanConfig[`${pce.id}-${selectedYear}`]?.enabled ? 'text-amber-800' : 'text-gray-500'}`}>
                              Premier bilan / Exercice atypique
                            </span>
                          </button>
                          {premierBilanConfig[`${pce.id}-${selectedYear}`]?.enabled && (
                            <div className="mt-1.5 ml-5 flex items-center gap-2">
                              <span className="text-xs text-teal-700">Début réel :</span>
                              <input
                                type="date"
                                value={premierBilanConfig[`${pce.id}-${selectedYear}`]?.startDate || ''}
                                onChange={(e) =>
                                  setPremierBilanConfig((prev) => ({
                                    ...prev,
                                    [`${pce.id}-${selectedYear}`]: {
                                      ...prev[`${pce.id}-${selectedYear}`],
                                      startDate: e.target.value,
                                    }
                                  }))
                                }
                                className="text-xs border border-teal-300 rounded px-1.5 py-0.5 bg-white"
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <p className="text-xs text-slate-500 italic text-center">Aucun PCE n'a été trouvé pour ce client.</p>
                )}
              </div>
            </WorkflowStepSection>

            {/* 4. CERFA 3310-TIC */}
            <WorkflowStepSection
              stepIndex={3}
              title="Étape 4 : CERFA 3310-TIC"
              subtitle="Génération du CERFA 3310-TIC-SD."
              icon={FiDownloadCloud}
              isActive={openStepIndex === 3}
              isOpen={openStepIndex === 3}
              onToggle={() => setOpenStepIndex(openStepIndex === 3 ? null : 3)}
            >
              <div className="bg-purple-50 border-l-4 border-purple-400 p-2 mb-3">
                <p className="text-xs text-purple-700">
                  <FiAlertTriangle className="inline h-3 w-3 mr-1 align-text-top" />
                  Assurez-vous que les CERFA ERA-G ont bien été générés pour la période.
                </p>
              </div>
              <button
                onClick={handleGenerateCerfa3310TIC}
                disabled={disableGenerate3310TicButton}
                className={`w-full sm:w-auto inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded shadow-sm text-white ${disableGenerate3310TicButton ? 'bg-slate-400 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700'}`}
              >
                {isGenerating3310TIC ? <FiLoader className="animate-spin h-4 w-4 mr-1.5" /> : <FiDownloadCloud className="h-4 w-4 mr-1.5" />}
                {isGenerating3310TIC ? 'Génération...' : cerfa3310TicGenerePourPeriode ? 'CERFA (3310-TIC) Généré' : 'Générer CERFA (3310-TIC)'}
              </button>
            </WorkflowStepSection>

            {/* 5. Finalisation & Validation */}
            <WorkflowStepSection
              stepIndex={4}
              title="Étape 5 : Finalisation et Validation"
              subtitle="Validation finale et confirmation de la génération."
              icon={FiCheckSquare}
              isActive={openStepIndex === 4}
              isOpen={openStepIndex === 4}
              onToggle={() => setOpenStepIndex(openStepIndex === 4 ? null : 4)}
            >
              <div className="space-y-3">
                <div className="p-3 bg-white rounded border border-gray-200 flex justify-between items-center">
                  <div>
                    <h4 className="text-sm font-medium text-slate-700">Action sur la Vérification des Documents</h4>
                    <p className="text-xs text-slate-500">Statut utilisateur: <span className="font-medium">{client.dossier_stage || 'N/A'}</span></p>
                  </div>
                  <button
                    onClick={handleMarkDocsVerified}
                    disabled={isMarkingVerificationComplete || !['cerfa_genere', 'cerfa_3310_genere', 'dossier_pret_signature'].includes(client.dossier_stage)}
                    className={`px-3 py-1.5 text-xs font-medium rounded flex items-center ${
                      (isMarkingVerificationComplete || !['cerfa_genere', 'cerfa_3310_genere', 'dossier_pret_signature'].includes(client.dossier_stage))
                      ? 'bg-gray-300 cursor-not-allowed'
                      : 'bg-green-600 text-white hover:bg-green-700'
                    }`}
                  >
                    {isMarkingVerificationComplete ? <FiLoader className="animate-spin h-3 w-3 mr-1" /> : <FiCheckSquare className="h-3 w-3 mr-1" />}
                    {client.admin_docs_verified_at ? 'Vérification Terminée' : 'Finaliser Vérification'}
                  </button>
                </div>

                {client && fiscalData && (
                  <div className={`p-3 border rounded ${dossierYear?.admin_cerfa_generation_completed_at ? 'bg-green-50 border-green-200' : 'bg-white border-gray-200'}`}>
                    {allIndividualCerfasGenerated ? (
                      !dossierYear?.admin_cerfa_generation_completed_at ? (
                        <div className="space-y-2">
                          <p className="text-xs text-slate-600">Tous les documents TICGN sont générés. Confirmez pour passer à l'étape de combinaison.</p>
                          <button onClick={handleConfirmCerfaGeneration} disabled={isConfirmingCerfaGen} className={`w-full sm:w-auto px-4 py-2 text-sm font-medium rounded shadow-sm text-white flex items-center justify-center ${isConfirmingCerfaGen ? 'bg-gray-400' : 'bg-emerald-600 hover:bg-emerald-700'}`}>
                            {isConfirmingCerfaGen ? <FiLoader className="animate-spin h-4 w-4 mr-1.5" /> : <FiCheck className="h-4 w-4 mr-1.5" />}
                            Confirmer Génération Terminée
                          </button>
                        </div>
                      ) : (
                        <p className="text-xs text-green-700 font-medium flex items-center">
                          <FiCheckSquare className="h-4 w-4 mr-1.5" />
                          Génération confirmée le {new Date(dossierYear.admin_cerfa_generation_completed_at).toLocaleDateString('fr-FR')}.
                        </p>
                      )
                    ) : (
                      <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3">
                        <p className="text-xs text-yellow-700">En attente de la génération de tous les documents CERFA TICGN.</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </WorkflowStepSection>

            {/* 6. Combinaison CERFAs */}
            <WorkflowStepSection
              stepIndex={5}
              title="Étape 6 : Combinaison des CERFAs"
              subtitle="Création d'un dossier PDF unique TICGN."
              icon={FiLayers}
              isActive={openStepIndex === 5}
              isOpen={openStepIndex === 5}
              onToggle={() => setOpenStepIndex(openStepIndex === 5 ? null : 5)}
            >
              {dossierYear?.admin_cerfa_generation_completed_at && !dossierYear?.cerfa_bundle_generated_at && (
                <div className="p-3 bg-gray-50 rounded border border-gray-200">
                  <p className="text-xs text-slate-600 mb-3">Génération confirmée. Combinez les documents en un seul PDF.</p>
                  <button onClick={handleCombineCerfas} disabled={isCombiningCerfas} className={`w-full sm:w-auto px-4 py-2 text-sm font-medium rounded shadow-sm text-white flex items-center justify-center ${isCombiningCerfas ? 'bg-gray-400' : 'bg-cyan-600 hover:bg-cyan-700'}`}>
                    {isCombiningCerfas ? <FiLoader className="animate-spin h-4 w-4 mr-1.5" /> : <FiLayers className="h-4 w-4 mr-1.5" />}
                    Combiner les CERFA TICGN
                  </button>
                </div>
              )}

              {dossierYear?.cerfa_bundle_generated_at && (
                <div className="p-3 bg-green-50 border border-green-200 rounded flex justify-between items-center">
                  <div>
                    <p className="text-xs text-green-700 font-medium flex items-center">
                      <FiCheckSquare className="h-4 w-4 mr-1.5" />
                      Dossier CERFA TICGN combiné créé le {new Date(dossierYear.cerfa_bundle_generated_at).toLocaleDateString('fr-FR')}.
                    </p>
                    <p className="text-[10px] text-green-600 mt-1 ml-6">Prêt pour l'envoi Yousign (Étape 7).</p>
                  </div>
                  <button
                    onClick={handleDeleteCerfaBundle}
                    className="p-2 text-red-500 hover:text-red-700 hover:bg-red-100 rounded-full transition-colors"
                    title="Supprimer le dossier combiné et recommencer"
                  >
                    <FiTrash2 className="h-4 w-4" />
                  </button>
                </div>
              )}
            </WorkflowStepSection>

            {/* 7. Signature Yousign */}
            <WorkflowStepSection
              stepIndex={6}
              title="Étape 7 : Signature Électronique"
              subtitle="Envoi du dossier TICGN via Yousign."
              icon={FiPenTool}
              isActive={openStepIndex === 6}
              isOpen={openStepIndex === 6}
              onToggle={() => setOpenStepIndex(openStepIndex === 6 ? null : 6)}
            >
              <div className="space-y-4">
                {dossierYear?.dossier_stage !== 'en_attente_signature' && dossierYear?.dossier_stage !== 'signe' && (
                  <div className="p-4 bg-white border rounded shadow-sm">
                    <p className="text-sm text-gray-600 mb-4">
                      Le dossier combiné TICGN est prêt. Cliquez ci-dessous pour créer une procédure de signature Yousign.
                    </p>
                    <button
                      onClick={handleSendYousignRequest}
                      disabled={isNotifying}
                      className="w-full sm:w-auto px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded hover:bg-indigo-700 flex items-center justify-center"
                    >
                      {isNotifying ? <FiLoader className="animate-spin mr-2"/> : <FiSend className="mr-2"/>}
                      Envoyer via Yousign
                    </button>
                  </div>
                )}

                {dossierYear?.dossier_stage === 'en_attente_signature' && (
                  <div className="p-4 bg-yellow-50 border border-yellow-200 rounded">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <FiClock className="h-5 w-5 text-yellow-600 mr-2"/>
                        <div>
                          <h4 className="text-sm font-bold text-yellow-800">En attente de signature</h4>
                          <p className="text-xs text-yellow-700 mt-1">La demande a été envoyée via Yousign.</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={handleCheckStatus}
                          className="px-3 py-1.5 bg-blue-50 border border-blue-200 text-blue-700 text-xs font-medium rounded hover:bg-blue-100 flex items-center transition-colors"
                          title="Forcer la vérification du statut"
                        >
                          <FiRefreshCw className="mr-1.5 h-3 w-3"/> Vérifier
                        </button>
                        <button
                          onClick={handleRetrySignature}
                          disabled={isNotifying}
                          className="px-3 py-1.5 bg-white border border-yellow-300 text-yellow-800 text-xs font-medium rounded hover:bg-yellow-100 flex items-center transition-colors"
                        >
                          {isNotifying ? <FiLoader className="animate-spin mr-1.5 h-3 w-3"/> : <FiSend className="mr-1.5 h-3 w-3"/>}
                          Relancer
                        </button>
                      </div>
                    </div>
                    <div className="mt-3 ml-7">
                      <p className="text-xs text-slate-500">ID Demande: {dossierYear?.yousign_request_id}</p>
                    </div>
                  </div>
                )}

                {dossierYear?.dossier_stage === 'signe' && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded">
                    <div className="flex items-center">
                      <FiCheckCircle className="h-5 w-5 text-green-600 mr-2"/>
                      <h4 className="text-sm font-bold text-green-800">Dossier TICGN Signé !</h4>
                    </div>
                    <p className="text-xs text-green-700 mt-1 ml-7">
                      Le dossier a été signé électroniquement le {dossierYear?.cerfa_signed_at ? new Date(dossierYear.cerfa_signed_at).toLocaleDateString() : 'N/A'}.
                    </p>
                    {!dossierYear?.signed_storage_path && (
                      <div className="mt-2 ml-7 flex items-center gap-2">
                        <p className="text-xs text-yellow-700">
                          <FiAlertTriangle className="inline h-3 w-3 mr-1" />
                          Fichier signé non encore disponible.
                        </p>
                        <button
                          onClick={handleCheckStatus}
                          className="px-2 py-1 bg-blue-50 border border-blue-200 text-blue-700 text-xs font-medium rounded hover:bg-blue-100 flex items-center"
                        >
                          <FiRefreshCw className="mr-1 h-3 w-3"/> Vérifier
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </WorkflowStepSection>

            {/* Documents CERFA Générés */}
            {generatedCerfas.length > 0 && (
              <SectionTitle
                title="Documents CERFA TICGN Générés"
                subtitle="Liste de tous les documents générés"
                icon={FiFileText}
                badge={generatedCerfas.length}
              >
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-slate-200">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Type</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Fichier</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Exercice</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-slate-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-slate-200">
                      {generatedCerfas.map((cerfa) => (
                        <tr key={cerfa.id}>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <div className="flex items-center space-x-2">
                              {cerfa.document_type === 'CERFA_BUNDLE' && <FiLayers className="h-4 w-4 text-indigo-600" />}
                              <span className="text-xs">{cerfa.document_type}</span>
                              {cerfa.document_type === 'CERFA_BUNDLE' && (
                                <span className="px-2 py-0.5 bg-purple-100 text-purple-800 text-xs font-medium rounded">Bundle</span>
                              )}
                            </div>
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-xs font-medium">
                            <button onClick={() => handleDownloadGeneratedCerfa(cerfa.storage_path, cerfa.file_name, cerfa.document_type)} className="underline text-slate-900 hover:text-blue-600">
                              {cerfa.file_name}
                            </button>
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-xs">
                            {cerfa.exercice_end_date ? new Date(cerfa.exercice_end_date).toLocaleDateString('fr-FR') : '—'}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-right text-xs space-x-1">
                            <button onClick={() => handlePreviewGeneratedCerfa(cerfa.storage_path, cerfa.document_type)} className="text-blue-600 hover:text-blue-800" title="Aperçu"><FiEye /></button>
                            <button onClick={() => handleDeleteGeneratedCerfa(cerfa.id, cerfa.storage_path, cerfa.document_type)} className="text-red-600 hover:text-red-800" title="Supprimer"><FiXSquare /></button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </SectionTitle>
            )}

            {/* Documents Signés */}
            {(() => {
              let signedDocsForUI = [...(signedCerfaDocuments || [])];
              if (signedDocsForUI.length === 0 && dossierYear?.signed_storage_path) {
                const sp = dossierYear.signed_storage_path;
                const fileName = sp.includes('/') ? sp.split('/').pop() : `DOSSIER_CERFA_TICGN_SIGNE_${selectedYear}.pdf`;
                signedDocsForUI = [{
                  id: `dossierYear-ticgn-${accountId}-${selectedYear}`,
                  storage_path: sp,
                  file_name: fileName,
                  signed_at: dossierYear.cerfa_signed_at || dossierYear.updated_at || null,
                }];
              }
              if (signedDocsForUI.length === 0) return null;
              return (
                <SectionTitle
                  title="Documents CERFA TICGN Signés"
                  subtitle="Documents validés et signés par le client"
                  icon={FiCheckSquare}
                  badge={signedDocsForUI.length}
                >
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-slate-200">
                      <tbody className="bg-white divide-y divide-slate-200">
                        {signedDocsForUI.map((docSigne) => (
                          <tr key={docSigne.id}>
                            <td className="px-3 py-2 whitespace-nowrap text-xs font-medium">
                              <button onClick={() => handleDownloadSignedCerfaBundle(docSigne.storage_path, docSigne.file_name)} className="underline text-slate-900 hover:text-blue-600">
                                {docSigne.file_name}
                              </button>
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-xs text-slate-700">
                              {docSigne.signed_at ? new Date(docSigne.signed_at).toLocaleDateString('fr-FR') : 'En attente'}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-right text-xs space-x-1">
                              <button onClick={() => handlePreviewSignedCerfaBundle(docSigne.storage_path)} className="text-blue-600 hover:text-blue-800" title="Aperçu"><FiEye /></button>
                              <button onClick={() => handleDownloadSignedCerfaBundle(docSigne.storage_path, docSigne.file_name)} className="text-green-600 hover:text-green-800" title="Télécharger"><FiDownload /></button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </SectionTitle>
              );
            })()}

          </div>
        )}
      </div>

      {/* Modale d'aperçu */}
      {isPreviewOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded shadow-xl max-w-4xl w-full h-[85vh] flex flex-col m-4">
            <div className="flex justify-between items-center p-3 border-b">
              <h2 className="text-sm font-medium">Aperçu du PDF</h2>
              <button onClick={() => { setIsPreviewOpen(false); setPreviewUrl(null); }} className="text-gray-500 hover:text-gray-700">
                <FiX className="h-4 w-4" />
              </button>
            </div>
            <div className="flex-1">
              {previewUrl ? (
                <iframe src={previewUrl} title="Aperçu PDF" className="w-full h-full border-0" />
              ) : (
                <p className="text-center p-6 text-sm">Chargement...</p>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Modal édition factures TICGN */}
      <TicgnInvoiceEditorModal
        open={isTicgnModalOpen}
        onClose={(saved) => {
          setIsTicgnModalOpen(false);
          setTicgnModalPce(null);
          if (saved) {
            toast.success("Champs enregistrés. Vous pouvez regénérer l'ERA-G.");
            fetchManualLinesCounts();
          }
        }}
        accountId={client?.account_id}
        pce={ticgnModalPce}
        year={selectedYear}
        adminUserId={session?.user?.id || null}
      />

      {/* Modale SIRET établissement + code APE par PCE */}
      <PdlSiretEditorModal
        open={isSiretModalOpen}
        onClose={() => {
          setIsSiretModalOpen(false);
          setSiretModalPce(null);
        }}
        pdl={siretModalPce}
        client={client}
        table="pce"
        onSaved={handlePceSaved}
      />

      {/* Modale édition raison sociale (accounts.company_name) */}
      <AccountRaisonSocialeEditorModal
        open={isRaisonSocialeModalOpen}
        onClose={() => setIsRaisonSocialeModalOpen(false)}
        client={client}
        onSaved={(updated) => setClient((prev) => ({ ...prev, ...updated }))}
      />
    </div>
  );
};

export default AnalyseTicgn;
